import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	IconButton,
	InputLabel,
	Menu,
	MenuItem,
	ListItemText,
	Paper,
	Select,
	Stack,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BlockIcon from '@mui/icons-material/Block';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import ViewCompactAltIcon from '@mui/icons-material/ViewCompactAlt';
import TableRowsIcon from '@mui/icons-material/TableRows';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import {
	confirmStage3AccessRequest,
	createStage3AccessRequestAdmin,
	createStage3ImportCopy,
	deleteStage3Document,
	expireStage3Invitations,
	getStage3AccessRequests,
	getStage3DocumentAdminStreamUrl,
	getStage3DocumentCommentsAdmin,
	getStage3Completeness,
	getStage3DocSettings,
	getStage3Documents,
	getStage3FileComments,
	getStage3FileIndex,
	getStage3SplitPreview,
	getOlympicsSettings,
	grantStage3Access,
	linkStage3Document,
	bulkLinkStage3FileIndex,
	rotateStage3DocumentCopy,
	rotateStage3FileIndexCopy,
	revokeStage3Access,
	resolveStage3StudentByWorkCode,
	resolveStage3StudentLookup,
	saveStage3DocSettings,
	scanStage3FileIndex,
	sendStage3Invitation,
	setStage3CompletenessAutoAccess,
	setStage3CompletenessVerified,
	setStage3SharedReview,
	setStage3FileCategory,
	setStage3JurorPointsByWorkCode,
	setStage3FileStatus,
	splitStage3CombinedReview,
	updateStage3Document,
	verifyStage3DocumentLinks,
} from '../../api/content-api';
import AuthContext from '../../store/auth-context';
import StudentWorkViewer, { type StudentWorkViewerAdminPreviewMeta } from '../UserPanel/StudentWorkViewer';
import {
	parseCommentOverlayOffsetX,
	parseCommentOverlayOffsetY,
	parseCommentOverlayRotationDeg,
	parseCommentOverlayScale,
	parseHideOverlayHighlightBoxes,
	parseHidePdfAnnotationRender,
	parseNotesObject,
	parseScalePromptInput,
	parseViewerPageRotations,
	parseViewerSoftRotationInput,
	parseCommentSoftRotationInput,
} from '../../utils/stage3ViewerNotes';

type Props = {
	open: boolean;
	onClose: () => void;
	token: string | null;
	onMessage: (msg: string) => void;
};

const Stage3DocumentAccessDialog = ({ open, onClose, token, onMessage }: Props) => {
	const { isAdmin } = useContext(AuthContext);
	const [tab, setTab] = useState(0);
	const [loading, setLoading] = useState(false);
	const [fileIndex, setFileIndex] = useState<any[]>([]);
	const [documents, setDocuments] = useState<any[]>([]);
	const [completeness, setCompleteness] = useState<any[]>([]);
	const [requests, setRequests] = useState<any[]>([]);
	const [settings, setSettings] = useState<{
		invitation_hours: number;
		access_minutes: number;
		watermark_template: string;
		view_enabled: boolean;
		request_enabled: boolean;
		ready_mail_subject: string;
		ready_mail_body: string;
	}>({
		invitation_hours: 48,
		access_minutes: 120,
		watermark_template: '{email} | {datetime}',
		view_enabled: true,
		request_enabled: true,
		ready_mail_subject: 'Twoja praca jest gotowa do wglądu ({work_code})',
		ready_mail_body:
			'Dzień dobry {first_name} {last_name},\n\nTwoja praca o kodzie {work_code} została skompletowana i jest gotowa do wglądu.\nZaloguj się do panelu i sprawdź status zgłoszenia.\n\nPozdrawiamy,\nBiuro Olimpiady',
	});
	const [newLink, setNewLink] = useState({
		student_id: '',
		work_code: '',
		document_type: 'review_1',
		file_index_id: '',
		drive_file_id: '',
		page_range: '',
	});
	const [selectedComments, setSelectedComments] = useState<any[]>([]);
	const [selectedCommentsFile, setSelectedCommentsFile] = useState<string>('');
	const [selectedCommentsMeta, setSelectedCommentsMeta] = useState<any | null>(null);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewItem, setPreviewItem] = useState<any | null>(null);
	const [previewMode, setPreviewMode] = useState<'preview' | 'view' | 'edit'>('preview');
	const [previewCursor, setPreviewCursor] = useState<number>(-1);
	const [previewItems, setPreviewItems] = useState<any[]>([]);
	const [previewScopeLabel, setPreviewScopeLabel] = useState<string>('Indeks plików');
	const [studentLikeViewerOpen, setStudentLikeViewerOpen] = useState(false);
	const [studentLikeDocumentId, setStudentLikeDocumentId] = useState<number | null>(null);
	const [studentLikeWatermark, setStudentLikeWatermark] = useState<string>('');
	const [studentLikeTitle, setStudentLikeTitle] = useState<string>('Podgląd pracy');
	const [studentLikePageRotations, setStudentLikePageRotations] = useState<Record<number, number>>({});
	const [studentLikeCommentOffsetY, setStudentLikeCommentOffsetY] = useState<Record<number, number>>({});
	const [studentLikeCommentOffsetYGlobal, setStudentLikeCommentOffsetYGlobal] = useState<number | null>(null);
	const [studentLikeCommentOffsetX, setStudentLikeCommentOffsetX] = useState<Record<number, number>>({});
	const [studentLikeCommentOffsetXGlobal, setStudentLikeCommentOffsetXGlobal] = useState<number | null>(null);
	const [studentLikeCommentScaleX, setStudentLikeCommentScaleX] = useState(100);
	const [studentLikeCommentScaleY, setStudentLikeCommentScaleY] = useState(100);
	const [studentLikeHideOverlayHighlightBoxes, setStudentLikeHideOverlayHighlightBoxes] = useState(false);
	const [studentLikeHidePdfAnnotationRender, setStudentLikeHidePdfAnnotationRender] = useState(false);
	const [studentLikeCommentRotationDeg, setStudentLikeCommentRotationDeg] = useState<Record<number, number>>({});
	const [studentLikeAdminMeta, setStudentLikeAdminMeta] = useState<StudentWorkViewerAdminPreviewMeta | null>(null);
	const [manualLinkMode, setManualLinkMode] = useState(false);
	const [splitPreviewOpen, setSplitPreviewOpen] = useState(false);
	const [splitPreviewRows, setSplitPreviewRows] = useState<any[]>([]);
	const [splitPreviewItem, setSplitPreviewItem] = useState<any | null>(null);
	const [splitPreviewError, setSplitPreviewError] = useState<string>('');
	const [splitPreviewAlerts, setSplitPreviewAlerts] = useState<string[]>([]);
	const [splitPreviewCorrections, setSplitPreviewCorrections] = useState<string[]>([]);
	const [splitPreviewForeignCodes, setSplitPreviewForeignCodes] = useState<string[]>([]);
	const [splitPreviewPotentialCodes, setSplitPreviewPotentialCodes] = useState<string[]>([]);
	const [splitPreviewSourceFileIndexId, setSplitPreviewSourceFileIndexId] = useState<number | null>(null);
	const [splitPreviewSourceDriveFileId, setSplitPreviewSourceDriveFileId] = useState<string>('');
	const [splitPreviewSourceDriveFileName, setSplitPreviewSourceDriveFileName] = useState<string>('');
	const [splitPreviewSourceDriveMimeType, setSplitPreviewSourceDriveMimeType] = useState<string>('');
	const [splitPreviewSelectedSegmentIds, setSplitPreviewSelectedSegmentIds] = useState<number[]>([]);
	const [splitPreviewExpandedSegmentIds, setSplitPreviewExpandedSegmentIds] = useState<number[]>([]);
	const [splitPreviewHtml, setSplitPreviewHtml] = useState<string>('');
	const [splitPreviewResultItems, setSplitPreviewResultItems] = useState<any[]>([]);
	const [splitPreviewResultAssignments, setSplitPreviewResultAssignments] = useState<any[]>([]);
	const [splitSummaryOpen, setSplitSummaryOpen] = useState(false);
	const [splitJuror1PointDrafts, setSplitJuror1PointDrafts] = useState<Record<number, string>>({});
	const [splitJuror2PointDrafts, setSplitJuror2PointDrafts] = useState<Record<number, string>>({});
	const [splitJurorPointSavingIds, setSplitJurorPointSavingIds] = useState<number[]>([]);
	const [splitPreviewBusy, setSplitPreviewBusy] = useState<'idle' | 'analyzing' | 'splitting'>('idle');
	const [splitAutoAssign, setSplitAutoAssign] = useState(false);
	const [splitAssignMode, setSplitAssignMode] = useState<'' | 'review_1' | 'review_2' | 'shared'>('');
	const [splitRemovePoints, setSplitRemovePoints] = useState(false);
	const [splitRemoveWorkCode, setSplitRemoveWorkCode] = useState(false);
	const [splitStripTermsRaw, setSplitStripTermsRaw] = useState('');
	const [splitPreviewShowSanitized, setSplitPreviewShowSanitized] = useState(false);
	const [showIndexCategoryHint, setShowIndexCategoryHint] = useState(true);
	const [showIndexAssignmentHint, setShowIndexAssignmentHint] = useState(true);
	const [thirdAMaxPoints, setThirdAMaxPoints] = useState<number>(30);
	const [indexViewPreset, setIndexViewPreset] = useState<'standard' | 'compact'>('compact');
	const [indexSelectedIds, setIndexSelectedIds] = useState<Record<number, boolean>>({});
	const [indexFilters, setIndexFilters] = useState({
		jury_pair_id: '',
		statuses: ['new'] as string[],
		query: '',
	});
	const [completenessFilters, setCompletenessFilters] = useState({
		jury_pair_id: '',
		query: '',
		requests: 'all' as 'all' | 'awaiting' | 'resolved',
		verification: 'all' as 'all' | 'none' | 'pending' | 'verified',
		completeness: 'all' as 'all' | 'yes' | 'missing',
	});
	const [docActionsAnchorEl, setDocActionsAnchorEl] = useState<null | HTMLElement>(null);
	const [docActionsContext, setDocActionsContext] = useState<{ row: any; docType: string } | null>(null);
	const [requestLookup, setRequestLookup] = useState('');

	const visibleRequests = useMemo(() => requests.slice(0, 200), [requests]);
	const getDriveViewUrl = (driveFileId: string) => `https://drive.google.com/file/d/${driveFileId}/view`;
	const getDriveEditUrl = (driveFileId: string) => `https://drive.google.com/file/d/${driveFileId}/edit`;
	const getDrivePreviewUrl = (driveFileId: string) => `https://drive.google.com/file/d/${driveFileId}/preview`;
	/**
	 * Indeks plików — edycja:
	 * - Natywny Google Doc: docs.google.com/.../edit
	 * - DOCX na Dysku: ten sam schemat co wyżej — Google otwiera Word w edytorze Dokumentów (Office compatibility); /preview tylko podgląda.
	 *   `usp=drivesdk` pomaga przy kontekście Dysku. Gdyby ten URL nie zadziałał, można ręcznie użyć drive.google.com/.../edit.
	 * - PDF i inne: klasyczny /edit na Drive.
	 */
	const getDriveEditUrlForIndexItem = (item: { drive_file_id?: string; drive_mime_type?: string }) => {
		const id = String(item?.drive_file_id || '').trim();
		if (!id) {
			return '';
		}
		const mime = String(item?.drive_mime_type || '').toLowerCase();
		if (mime === 'application/vnd.google-apps.document') {
			return `https://docs.google.com/document/d/${encodeURIComponent(id)}/edit`;
		}
		if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
			return `https://docs.google.com/document/d/${encodeURIComponent(id)}/edit?usp=drivesdk`;
		}
		return getDriveEditUrl(id);
	};
	const getDriveEditTooltipForIndexItem = (item: { drive_mime_type?: string }) => {
		const mime = String(item?.drive_mime_type || '').toLowerCase();
		if (mime === 'application/vnd.google-apps.document') {
			return 'Edytuj w Dokumentach Google';
		}
		if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
			return 'Edytuj w Dokumentach Google (plik Word z Dysku — tryb kompatybilności Office)';
		}
		return 'Edytuj plik na Drive';
	};
	const normalizeCode = (value: string) =>
		value
			.trim()
			.toUpperCase()
			.replace(/[^A-Z0-9]/g, '');
	const fileIndexById = useMemo(() => {
		const m = new Map<number, any>();
		(fileIndex || []).forEach((item) => m.set(Number(item.id), item));
		return m;
	}, [fileIndex]);
	const documentBySourceIndexId = useMemo(() => {
		const m = new Map<number, any>();
		(documents || []).forEach((doc: any) => {
			const key = Number(doc?.source_file_index_id || 0);
			if (key > 0 && !m.has(key)) {
				m.set(key, doc);
			}
		});
		return m;
	}, [documents]);
	const documentsById = useMemo(() => {
		const m = new Map<number, any>();
		(documents || []).forEach((doc: any) => m.set(Number(doc.id), doc));
		return m;
	}, [documents]);
	const juryPairOptions = useMemo(() => {
		const map = new Map<number, string>();
		(fileIndex || []).forEach((item) => {
			const id = Number(item?.jury_pair_id || 0);
			const name = String(item?.jury_pair_name || '').trim();
			if (id > 0 && name !== '' && !map.has(id)) {
				map.set(id, name);
			}
		});
		return Array.from(map.entries())
			.map(([id, name]) => ({ id, name }))
			.sort((a, b) => a.name.localeCompare(b.name, 'pl'));
	}, [fileIndex]);
	const statusOptions = ['new', 'split', 'assigned', 'rejected', 'obsolete', 'docx_original'];
	const categoryOptions = ['unknown', 'review', 'combined_review', 'reviewed', 'original'];
	const compactIndexView = indexViewPreset === 'compact';
	const filteredFileIndex = useMemo(() => {
		const q = indexFilters.query.trim().toLowerCase();
		return (fileIndex || []).filter((item) => {
			const status = String(item?.file_status || '');
			if (indexFilters.statuses.length > 0 && !indexFilters.statuses.includes(status)) {
				return false;
			}
			if (indexFilters.jury_pair_id && Number(item?.jury_pair_id || 0) !== Number(indexFilters.jury_pair_id)) {
				return false;
			}
			if (!q) {
				return true;
			}
			const haystack = [
				String(item?.drive_file_name || ''),
				String(item?.drive_parent_path || ''),
				String(item?.matched_work_code || ''),
			]
				.join(' ')
				.toLowerCase();
			return haystack.includes(q);
		});
	}, [fileIndex, indexFilters]);
	const selectedFilteredFileItems = useMemo(
		() => filteredFileIndex.filter((item) => Boolean(indexSelectedIds[Number(item.id)])),
		[filteredFileIndex, indexSelectedIds],
	);
	const allFilteredSelected =
		filteredFileIndex.length > 0 && selectedFilteredFileItems.length === filteredFileIndex.length;
	useEffect(() => {
		const allowedIds = new Set(filteredFileIndex.map((item) => Number(item.id)));
		setIndexSelectedIds((prev) => {
			const next: Record<number, boolean> = {};
			Object.entries(prev).forEach(([idRaw, value]) => {
				const id = Number(idRaw);
				if (value && allowedIds.has(id)) {
					next[id] = true;
				}
			});
			return next;
		});
	}, [filteredFileIndex]);
	const filteredCompleteness = useMemo(() => {
		const q = completenessFilters.query.trim().toLowerCase();
		const awaitingStatuses = [
			'pending',
			'collecting',
			'ready',
			'confirmed',
			'notified',
			'invitation_expired',
			'paper_only',
		];
		const resolvedStatuses = ['active', 'expired'];
		return (completeness || []).filter((row) => {
			if (
				completenessFilters.jury_pair_id &&
				Number(row?.assigned_jury_pair_id || 0) !== Number(completenessFilters.jury_pair_id)
			) {
				return false;
			}
			if (q) {
				const haystack = `${String(row?.student_name || '')} ${String(row?.work_code || '')}`.toLowerCase();
				if (!haystack.includes(q)) {
					return false;
				}
			}
			const requestStatus = String(row?.request_status || '');
			const hasRequest = Boolean(row?.has_access_request);
			if (completenessFilters.requests === 'awaiting') {
				if (!hasRequest || !awaitingStatuses.includes(requestStatus)) {
					return false;
				}
			}
			if (completenessFilters.requests === 'resolved') {
				if (!hasRequest || !resolvedStatuses.includes(requestStatus)) {
					return false;
				}
			}
			if (completenessFilters.verification === 'none') {
				if (hasRequest) {
					return false;
				}
			}
			if (completenessFilters.verification === 'pending') {
				if (!hasRequest || Boolean(row?.access_verified)) {
					return false;
				}
			}
			if (completenessFilters.verification === 'verified') {
				if (!Boolean(row?.access_verified)) {
					return false;
				}
			}
			if (completenessFilters.completeness === 'yes' && !Boolean(row?.is_complete)) {
				return false;
			}
			if (completenessFilters.completeness === 'missing' && Boolean(row?.is_complete)) {
				return false;
			}
			return true;
		});
	}, [completeness, completenessFilters]);

	const loadAll = async () => {
		setLoading(true);
		try {
			const [indexRes, docsRes, compRes, reqRes, settingsRes, olympicsRes] = await Promise.all([
				getStage3FileIndex(token),
				getStage3Documents(token),
				getStage3Completeness(token),
				getStage3AccessRequests(token),
				getStage3DocSettings(token),
				getOlympicsSettings(token),
			]);
			setFileIndex(indexRes?.data?.items || []);
			setDocuments(docsRes?.data?.items || []);
			setCompleteness(compRes?.data?.items || []);
			setRequests(reqRes?.data?.items || []);
			setSettings(
				settingsRes?.data || {
					invitation_hours: 48,
					access_minutes: 120,
					watermark_template: '{email} | {datetime}',
					view_enabled: true,
					request_enabled: true,
					ready_mail_subject: 'Twoja praca jest gotowa do wglądu ({work_code})',
					ready_mail_body:
						'Dzień dobry {first_name} {last_name},\n\nTwoja praca o kodzie {work_code} została skompletowana i jest gotowa do wglądu.\nZaloguj się do panelu i sprawdź status zgłoszenia.\n\nPozdrawiamy,\nBiuro Olimpiady',
				},
			);
			const olympics = olympicsRes?.data || {};
			const detectedMax =
				Number(olympics?.thirdStagePoints?.partA?.maxPoints) ||
				Number(olympics?.third_a_stage_max_points) ||
				Number(olympics?.thirdAStageMaxPoints) ||
				30;
			setThirdAMaxPoints(Number.isFinite(detectedMax) && detectedMax > 0 ? detectedMax : 30);
		} catch (error: any) {
			onMessage(error?.response?.data?.message || 'Nie udało się pobrać danych.');
		} finally {
			setLoading(false);
		}
	};

	const reloadScope = async (
		scope:
			| 'none'
			| 'index'
			| 'documents'
			| 'completeness'
			| 'requests'
			| 'completeness+requests'
			| 'docs+index+completeness'
			| 'all',
	) => {
		if (scope === 'none') {
			return;
		}
		if (scope === 'all') {
			await loadAll();
			return;
		}
		const tasks: Array<Promise<any>> = [];
		if (scope === 'index' || scope === 'docs+index+completeness') {
			tasks.push(
				getStage3FileIndex(token).then((res) => {
					setFileIndex(res?.data?.items || []);
				}),
			);
		}
		if (scope === 'documents' || scope === 'docs+index+completeness') {
			tasks.push(
				getStage3Documents(token).then((res) => {
					setDocuments(res?.data?.items || []);
				}),
			);
		}
		if (scope === 'completeness' || scope === 'completeness+requests' || scope === 'docs+index+completeness') {
			tasks.push(
				getStage3Completeness(token).then((res) => {
					setCompleteness(res?.data?.items || []);
				}),
			);
		}
		if (scope === 'requests' || scope === 'completeness+requests') {
			tasks.push(
				getStage3AccessRequests(token).then((res) => {
					setRequests(res?.data?.items || []);
				}),
			);
		}
		if (tasks.length > 0) {
			await Promise.all(tasks);
		}
	};

	useEffect(() => {
		if (open) {
			loadAll();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const action = async (
		fn: () => Promise<any>,
		msg: string,
		scope:
			| 'none'
			| 'index'
			| 'documents'
			| 'completeness'
			| 'requests'
			| 'completeness+requests'
			| 'docs+index+completeness'
			| 'all' = 'all',
	) => {
		setLoading(true);
		try {
			const response = await fn();
			onMessage(msg);
			await reloadScope(scope);
			return response;
		} catch (error: any) {
			onMessage(error?.response?.data?.message || error?.message || 'Operacja nie powiodła się.');
		} finally {
			setLoading(false);
		}
	};

	const openPreviewPanel = (
		item: any,
		mode: 'preview' | 'view' | 'edit' = 'preview',
		items: any[] = filteredFileIndex,
		scopeLabel: string = 'Indeks plików',
	) => {
		setPreviewItem(item);
		setPreviewMode(mode);
		setPreviewOpen(true);
		setPreviewItems(items);
		setPreviewScopeLabel(scopeLabel);
		setPreviewCursor(items.findIndex((row) => Number(row.id) === Number(item.id)));
	};

	const getPreviewSrc = () => {
		if (!previewItem?.drive_file_id) {
			return '';
		}
		return getDrivePreviewUrl(previewItem.drive_file_id);
	};

	const resolveAndFillStudent = async (workCodeRaw: string): Promise<number | null> => {
		const workCode = normalizeCode(workCodeRaw);
		if (!workCode) return null;
		try {
			const response = await resolveStage3StudentByWorkCode(token, workCode);
			const item = response?.data?.item;
			if (item?.student_id) {
				setNewLink((prev) => ({
					...prev,
					student_id: String(item.student_id),
					work_code: String(item.work_code || workCode),
				}));
				return Number(item.student_id);
			}
		} catch (_err) {
			// ignored intentionally
		}
		return null;
	};

	const fillLinkByCode = async (item: any) => {
		const code = String(item?.matched_work_code || '').trim();
		setNewLink((prev) => ({
			...prev,
			file_index_id: String(item.id),
			drive_file_id: item.drive_file_id || '',
			work_code: code,
		}));
		setManualLinkMode(false);
		setTab(1);
		const sid = await resolveAndFillStudent(code);
		if (!sid) {
			onMessage(`Nie znaleziono automatycznie ucznia dla kodu pracy: ${code || '-'}.`);
		}
	};
	const getStudentPreviewItems = (row: any): any[] => {
		const mapping: Array<{ key: string; label: string }> = [
			{ key: 'original', label: 'Praca oryginalna' },
			{ key: 'reviewed_scan', label: 'Skan pracy sprawdzonej' },
			{ key: 'review_1', label: 'Recenzja jurora 1' },
			{ key: 'review_2', label: 'Recenzja jurora 2' },
			{ key: 'extra', label: 'Dokument dodatkowy' },
		];
		const result: any[] = [];
		mapping.forEach(({ key, label }) => {
			const docs = Array.isArray(row?.documents_by_type?.[key]) ? row.documents_by_type[key] : [];
			docs.forEach((doc: any) => {
				if (!doc?.drive_file_id) {
					return;
				}
				result.push({
					id: Number(doc.id),
					document_id: Number(doc.id),
					document_type: key,
					document_type_label: label,
					student_id: Number(row.student_id),
					student_name: String(row.student_name || ''),
					work_code: String(row.work_code || ''),
					drive_file_id: String(doc.drive_file_id || ''),
					drive_file_name: String(doc.drive_file_name || ''),
					drive_mime_type: String(doc.drive_mime_type || ''),
					jury_pair_name: String(doc.source_jury_pair_name || ''),
					drive_parent_path: '',
					source_file_index_id: doc.source_file_index_id ? Number(doc.source_file_index_id) : null,
					preview_context: 'completeness' as const,
					third_a_stage_points: row.third_a_stage_points ?? null,
					third_a_juror1_points: row.third_a_juror1_points ?? null,
					third_a_juror2_points: row.third_a_juror2_points ?? null,
					shared_review: Boolean(row.shared_review),
					assigned_jury_pair_name: String(row.assigned_jury_pair_name || ''),
					assigned_juror1_name: String(row.assigned_juror1_name || ''),
					assigned_juror2_name: String(row.assigned_juror2_name || ''),
				});
			});
		});
		return result;
	};
	const openCompletenessDocument = (row: any, docType: string) => {
		const docs = Array.isArray(row?.documents_by_type?.[docType]) ? row.documents_by_type[docType] : [];
		const first = docs[0];
		if (!first?.drive_file_id) {
			return;
		}
		const list = getStudentPreviewItems(row);
		const selected = list.find((item) => Number(item.document_id) === Number(first.id)) || null;
		if (!selected) {
			return;
		}
		openPreviewPanel(selected, 'view', list, `Dokumenty ucznia: ${row.student_name || row.student_id}`);
	};
	const openCompletenessStudentLikePreview = (row: any, docType: string) => {
		const docs = Array.isArray(row?.documents_by_type?.[docType]) ? row.documents_by_type[docType] : [];
		const first = docs[0];
		if (!first?.id) {
			return;
		}
		const notesField = first?.notes;
		setStudentLikeDocumentId(Number(first.id));
		setStudentLikeTitle(`Podgląd: ${docTypeLabel(docType)}`);
		setStudentLikePageRotations(parseViewerPageRotations(notesField));
		const co = parseCommentOverlayOffsetY(notesField);
		setStudentLikeCommentOffsetY(co.byPage);
		setStudentLikeCommentOffsetYGlobal(co.global);
		const cox = parseCommentOverlayOffsetX(notesField);
		setStudentLikeCommentOffsetX(cox.byPage);
		setStudentLikeCommentOffsetXGlobal(cox.global);
		const sc = parseCommentOverlayScale(notesField);
		setStudentLikeCommentScaleX(sc.x);
		setStudentLikeCommentScaleY(sc.y);
		setStudentLikeHideOverlayHighlightBoxes(parseHideOverlayHighlightBoxes(notesField));
		setStudentLikeHidePdfAnnotationRender(parseHidePdfAnnotationRender(notesField));
		setStudentLikeCommentRotationDeg(parseCommentOverlayRotationDeg(notesField));
		setStudentLikeAdminMeta(
			isAdmin
				? {
						work_code: String(row?.work_code || ''),
						student_name: String(row?.student_name || ''),
						third_a_stage_points: row?.third_a_stage_points ?? null,
						third_a_juror1_points: row?.third_a_juror1_points ?? null,
						third_a_juror2_points: row?.third_a_juror2_points ?? null,
						shared_review: Boolean(row?.shared_review),
						assigned_juror1_name: String(row?.assigned_juror1_name || ''),
						assigned_juror2_name: String(row?.assigned_juror2_name || ''),
						assigned_jury_pair_name: String(row?.assigned_jury_pair_name || ''),
						document_type: docType,
					}
				: null,
		);
		setStudentLikeWatermark(
			buildWatermarkLabel(settings.watermark_template, {
				email: String(row?.student_email || row?.email || 'biuro'),
				datetime: new Date().toLocaleString(),
				student_name: String(row?.student_name || row?.student_id || '-'),
				work_code: String(row?.work_code || ''),
				doc_type: String(docType || ''),
			}),
		);
		setStudentLikeViewerOpen(true);
	};
	const editCompletenessPageRotations = async (row: any, docType: string) => {
		const docs = Array.isArray(row?.documents_by_type?.[docType]) ? row.documents_by_type[docType] : [];
		const first = docs[0];
		if (!first?.id) {
			onMessage('Brak dokumentu do ustawienia obrotu.');
			return;
		}
		const current = parseViewerPageRotations(first?.notes);
		const currentHint = Object.keys(current)
			.sort((a, b) => Number(a) - Number(b))
			.map((k) => `${k}:${current[Number(k)]}`)
			.join(',');
		const input = window.prompt(
			'Dodatkowy obrót widoku (0/90/180/270°) — ten sam dla obrazu i komentarzy. Format: 1:90,2:270 lub same numery stron (= 180°). Puste = wyczyść.',
			currentHint,
		);
		if (input === null) {
			return;
		}
		const nextMap = parseViewerSoftRotationInput(input);
		let notesObj: Record<string, unknown> = { ...(parseNotesObject(first?.notes) || {}) };
		if (Object.keys(nextMap).length > 0) {
			notesObj.viewer_page_rotations = nextMap;
		} else {
			delete notesObj.viewer_page_rotations;
		}
		const notesText = Object.keys(notesObj || {}).length > 0 ? JSON.stringify(notesObj) : '';
		await action(
			() => updateStage3Document(token, Number(first.id), { notes: notesText }),
			'Zapisano ustawienia obrotu widoku (safe podgląd).',
			'completeness+requests',
		);
	};
	const editCompletenessCommentOffsetY = async (row: any, docType: string) => {
		const docs = Array.isArray(row?.documents_by_type?.[docType]) ? row.documents_by_type[docType] : [];
		const first = docs[0];
		if (!first?.id) {
			onMessage('Brak dokumentu do ustawienia przesunięcia.');
			return;
		}
		const co = parseCommentOverlayOffsetY(first?.notes);
		const defaultHint =
			co.global != null ? String(co.global) : Object.keys(co.byPage).length > 0 ? JSON.stringify(co.byPage) : '';
		const input = window.prompt(
			'Przesunięcie żółtych podświetleń w pionie (px; + = w dół). Jedna liczba dla wszystkich stron albo JSON strona→px, np. {"1":10}. Puste = wyłącz.',
			defaultHint,
		);
		if (input === null) {
			return;
		}
		let notesObj: Record<string, unknown> = { ...(parseNotesObject(first?.notes) || {}) };
		const trimmed = String(input).trim();
		if (trimmed === '') {
			delete notesObj.comment_overlay_offset_y;
		} else if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
			notesObj.comment_overlay_offset_y = Number(trimmed);
		} else {
			try {
				const parsed = JSON.parse(trimmed);
				if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
					onMessage('Oczekiwano obiektu JSON strona→px.');
					return;
				}
				notesObj.comment_overlay_offset_y = parsed;
			} catch {
				onMessage('Niepoprawny JSON.');
				return;
			}
		}
		const notesText = Object.keys(notesObj || {}).length > 0 ? JSON.stringify(notesObj) : '';
		await action(
			() => updateStage3Document(token, Number(first.id), { notes: notesText }),
			'Zapisano przesunięcie komentarzy (komY).',
			'completeness+requests',
		);
	};
	const editCompletenessCommentOffsetX = async (row: any, docType: string) => {
		const docs = Array.isArray(row?.documents_by_type?.[docType]) ? row.documents_by_type[docType] : [];
		const first = docs[0];
		if (!first?.id) {
			onMessage('Brak dokumentu do ustawienia przesunięcia.');
			return;
		}
		const co = parseCommentOverlayOffsetX(first?.notes);
		const defaultHint =
			co.global != null ? String(co.global) : Object.keys(co.byPage).length > 0 ? JSON.stringify(co.byPage) : '';
		const input = window.prompt(
			'Przesunięcie żółtych podświetleń w poziomie (px; + = w prawo). Jedna liczba dla wszystkich stron albo JSON strona→px, np. {"1":10}. Puste = wyłącz.',
			defaultHint,
		);
		if (input === null) {
			return;
		}
		let notesObj: Record<string, unknown> = { ...(parseNotesObject(first?.notes) || {}) };
		const trimmed = String(input).trim();
		if (trimmed === '') {
			delete notesObj.comment_overlay_offset_x;
		} else if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
			notesObj.comment_overlay_offset_x = Number(trimmed);
		} else {
			try {
				const parsed = JSON.parse(trimmed);
				if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
					onMessage('Oczekiwano obiektu JSON strona→px.');
					return;
				}
				notesObj.comment_overlay_offset_x = parsed;
			} catch {
				onMessage('Niepoprawny JSON.');
				return;
			}
		}
		const notesText = Object.keys(notesObj || {}).length > 0 ? JSON.stringify(notesObj) : '';
		await action(
			() => updateStage3Document(token, Number(first.id), { notes: notesText }),
			'Zapisano przesunięcie komentarzy (komX).',
			'completeness+requests',
		);
	};
	const editCompletenessCommentScale = async (row: any, docType: string) => {
		const docs = Array.isArray(row?.documents_by_type?.[docType]) ? row.documents_by_type[docType] : [];
		const first = docs[0];
		if (!first?.id) {
			onMessage('Brak dokumentu do ustawienia skali.');
			return;
		}
		const cur = parseCommentOverlayScale(first?.notes);
		const currentHint = `${cur.x},${cur.y}`;
		const input = window.prompt(
			'Skala prostokątów komentarzy od środka strony (X%, Y%), np. 70,150 lub jedna liczba dla obu osi. Puste = przywróć domyślną (usuń z notes).',
			currentHint,
		);
		if (input === null) {
			return;
		}
		let notesObj: Record<string, unknown> = { ...(parseNotesObject(first?.notes) || {}) };
		const trimmed = String(input).trim();
		if (trimmed === '') {
			delete notesObj.comment_overlay_scale;
		} else {
			const parsed = parseScalePromptInput(trimmed);
			if (!parsed) {
				onMessage('Niepoprawny format. Użyj np. 70,150 lub 90.');
				return;
			}
			notesObj.comment_overlay_scale = { x: parsed.x, y: parsed.y };
		}
		const notesText = Object.keys(notesObj || {}).length > 0 ? JSON.stringify(notesObj) : '';
		await action(
			() => updateStage3Document(token, Number(first.id), { notes: notesText }),
			'Zapisano skalę nakładki komentarzy.',
			'completeness+requests',
		);
	};
	const editCompletenessToggleHideOverlayHighlightBoxes = async (row: any, docType: string) => {
		const docs = Array.isArray(row?.documents_by_type?.[docType]) ? row.documents_by_type[docType] : [];
		const first = docs[0];
		if (!first?.id) {
			onMessage('Brak dokumentu.');
			return;
		}
		const current = parseHideOverlayHighlightBoxes(first?.notes);
		const next = !current;
		const notesObj: Record<string, unknown> = { ...(parseNotesObject(first?.notes) || {}) };
		if (next) {
			notesObj.hide_overlay_highlight_boxes = true;
		} else {
			delete notesObj.hide_overlay_highlight_boxes;
		}
		const notesText = Object.keys(notesObj || {}).length > 0 ? JSON.stringify(notesObj) : '';
		await action(
			() => updateStage3Document(token, Number(first.id), { notes: notesText }),
			next ? 'Ukryto żółte prostokąty (nakładka HTML).' : 'Przywrócono żółte prostokąty (nakładka HTML).',
			'completeness+requests',
		);
	};
	const editCompletenessToggleHidePdfAnnotationRender = async (row: any, docType: string) => {
		const docs = Array.isArray(row?.documents_by_type?.[docType]) ? row.documents_by_type[docType] : [];
		const first = docs[0];
		if (!first?.id) {
			onMessage('Brak dokumentu.');
			return;
		}
		const current = parseHidePdfAnnotationRender(first?.notes);
		const next = !current;
		const notesObj: Record<string, unknown> = { ...(parseNotesObject(first?.notes) || {}) };
		if (next) {
			notesObj.hide_pdf_annotation_render = true;
		} else {
			delete notesObj.hide_pdf_annotation_render;
		}
		const notesText = Object.keys(notesObj || {}).length > 0 ? JSON.stringify(notesObj) : '';
		await action(
			() => updateStage3Document(token, Number(first.id), { notes: notesText }),
			next
				? 'Wyłączono rysowanie adnotacji PDF na obrazie strony (pdf.js).'
				: 'Przywrócono rysowanie adnotacji PDF na obrazie strony.',
			'completeness+requests',
		);
	};
	const editCompletenessCommentRotationOnly = async (row: any, docType: string) => {
		const docs = Array.isArray(row?.documents_by_type?.[docType]) ? row.documents_by_type[docType] : [];
		const first = docs[0];
		if (!first?.id) {
			onMessage('Brak dokumentu do ustawienia obrotu komentarzy.');
			return;
		}
		const current = parseCommentOverlayRotationDeg(first?.notes);
		const currentHint = Object.keys(current)
			.sort((a, b) => Number(a) - Number(b))
			.map((k) => `${k}:${current[Number(k)]}`)
			.join(',');
		const input = window.prompt(
			'Obrót tylko warstwy komentarzy (obraz bez zmian). Format jak przy widoku: 1:90,2:270 lub same numery (= 180°). Puste = wyczyść.',
			currentHint,
		);
		if (input === null) {
			return;
		}
		const nextMap = parseCommentSoftRotationInput(input);
		let notesObj: Record<string, unknown> = { ...(parseNotesObject(first?.notes) || {}) };
		if (Object.keys(nextMap).length > 0) {
			notesObj.comment_overlay_rotation_deg = nextMap;
		} else {
			delete notesObj.comment_overlay_rotation_deg;
		}
		const notesText = Object.keys(notesObj || {}).length > 0 ? JSON.stringify(notesObj) : '';
		await action(
			() => updateStage3Document(token, Number(first.id), { notes: notesText }),
			'Zapisano obrót komentarzy (obraz bez zmian).',
			'completeness+requests',
		);
	};
	const createAggressiveRotatedCopy = async (row: any, docType: string) => {
		const docs = Array.isArray(row?.documents_by_type?.[docType]) ? row.documents_by_type[docType] : [];
		const first = docs[0];
		if (!first?.id) {
			onMessage('Brak dokumentu do wykonania kopii obrotu.');
			return;
		}
		const input = window.prompt(
			'Kopia agresywna: podaj numery stron do trwałego obrotu o 180° (oddzielone przecinkami), np. 2,4,7.',
			'',
		);
		if (input === null) {
			return;
		}
		const pages = Object.keys(parseRotationInput(input))
			.map((x) => Number(x))
			.filter((x) => Number.isFinite(x) && x > 0);
		if (pages.length === 0) {
			onMessage('Brak stron do obrotu.');
			return;
		}
		const shouldReplace = window.confirm('Podmienić bieżące powiązanie dokumentu na nową obróconą kopię PDF?');
		await action(
			() => rotateStage3DocumentCopy(token, Number(first.id), { rotate_pages: pages, replace_current: shouldReplace }),
			shouldReplace
				? 'Utworzono kopię obróconą i podmieniono dokument.'
				: 'Utworzono kopię obróconą (bez podmiany dokumentu).',
		);
	};
	const openDocActionsMenu = (event: React.MouseEvent<HTMLElement>, row: any, docType: string) => {
		setDocActionsAnchorEl(event.currentTarget);
		setDocActionsContext({ row, docType });
	};
	const closeDocActionsMenu = () => {
		setDocActionsAnchorEl(null);
		setDocActionsContext(null);
	};
	const getCompletenessDoc = (row: any, docType: string) => {
		const docs = Array.isArray(row?.documents_by_type?.[docType]) ? row.documents_by_type[docType] : [];
		return docs[0] || null;
	};
	const renderCompletenessDocCell = (row: any, docType: string) => {
		const doc = getCompletenessDoc(row, docType);
		if (!doc?.id) {
			return '-brak-';
		}
		return (
			<Tooltip title={String(doc?.drive_file_name || docTypeLabel(docType))}>
				<Button
					size='small'
					variant='outlined'
					onClick={(event) => openDocActionsMenu(event, row, docType)}
				>
					POKAŻ
				</Button>
			</Tooltip>
		);
	};
	const createAggressiveRotatedCopyFromIndex = async (item: any) => {
		const input = window.prompt(
			'Kopia agresywna: podaj mapę rotacji, np. 2:90,3:270,4:180 (lub same numery: 2,4 = 180°).',
			'',
		);
		if (input === null) {
			return;
		}
		const pageRotations = parseAggressiveRotationInput(input);
		if (Object.keys(pageRotations).length === 0) {
			onMessage('Brak poprawnej mapy stron do obrotu (90/180/270).');
			return;
		}
		const shouldReplace = window.confirm('Podmienić bieżący plik indexu na nową obróconą kopię PDF?');
		await action(
			() =>
				rotateStage3FileIndexCopy(token, Number(item.id), {
					page_rotations: pageRotations,
					replace_current: shouldReplace,
				}),
			shouldReplace
				? 'Utworzono kopię obróconą i podmieniono plik indexu.'
				: 'Utworzono kopię obróconą w indexie (bez podmiany źródła).',
		);
	};
	const editDocumentAssignment = async (row: any) => {
		await action(async () => {
			const nextType =
				window.prompt('Typ dokumentu (original/reviewed_scan/review_1/review_2/extra):', row.document_type || '') ||
				row.document_type;
			const nextRange = window.prompt('Nowy zakres stron (np. 1-2):', row.page_range || '') || '';
			return updateStage3Document(token, Number(row.id), {
				document_type: nextType,
				page_range: nextRange,
			});
		}, 'Zaktualizowano przypisanie dokumentu.');
	};

	const openCompletenessGoogleDocsEdit = (row: any, docType: string) => {
		const doc = getCompletenessDoc(row, docType);
		if (!doc?.drive_file_id) {
			onMessage('Brak pliku do edycji.');
			return;
		}
		const url = getDriveEditUrlForIndexItem({
			drive_file_id: doc.drive_file_id,
			drive_mime_type: doc.drive_mime_type,
		});
		if (!url) {
			onMessage('Nie udało się zbudować adresu edycji (Google).');
			return;
		}
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	const editCompletenessDocAssignment = async (row: any, docType: string) => {
		const doc = getCompletenessDoc(row, docType);
		if (!doc?.id) {
			onMessage('Brak dokumentu do edycji przypisania.');
			return;
		}
		await action(
			async () => {
				const nextType =
					window.prompt(
						'Typ dokumentu (original/reviewed_scan/review_1/review_2/extra):',
						String(doc.document_type || ''),
					) || String(doc.document_type || '');
				const nextRange = window.prompt('Zakres stron (np. 1-2, puste = brak):', String(doc.page_range || '')) || '';
				return updateStage3Document(token, Number(doc.id), {
					document_type: nextType,
					page_range: nextRange,
				});
			},
			'Zaktualizowano przypisanie dokumentu.',
			'completeness+requests',
		);
	};

	const removeCompletenessDocumentLink = async (row: any, docType: string) => {
		const doc = getCompletenessDoc(row, docType);
		if (!doc?.id) {
			onMessage('Brak dokumentu do usunięcia.');
			return;
		}
		if (
			!window.confirm(
				'Usunąć powiązanie tego dokumentu z uczniem? Możesz je później odtworzyć ponownym przypisaniem w zakładce Dokumenty.',
			)
		) {
			return;
		}
		await action(
			() => deleteStage3Document(token, Number(doc.id)),
			'Usunięto powiązanie dokumentu.',
			'completeness+requests',
		);
	};

	const docTypeLabel = (type: string) => {
		const labels: Record<string, string> = {
			original: 'Praca oryginalna',
			reviewed_scan: 'Skan pracy sprawdzonej',
			review_1: 'Recenzja jurora 1',
			review_2: 'Recenzja jurora 2',
			extra: 'Dokument dodatkowy',
		};
		return labels[type] || type;
	};
	const buildWatermarkLabel = (templateRaw: string, values: Record<string, string>) => {
		const template = String(templateRaw || '').trim();
		const source = template !== '' ? template : '{email} | {datetime}';
		const rendered = source.replace(/\{([a-z_]+)\}/gi, (_all, keyRaw: string) => {
			const key = String(keyRaw || '').toLowerCase();
			return String(values[key] || '');
		});
		const parts = rendered.split('|').filter((part) => part.replace(/\s/g, '').length > 0);
		if (parts.length > 0) {
			return parts.join('|');
		}
		return `${values.email || values.student_name || 'uczeń'}|${values.datetime || ''}`;
	};
	const parseRotationInput = (raw: string): Record<number, number> => {
		const result: Record<number, number> = {};
		const tokens = String(raw || '')
			.split(',')
			.map((x) => Number(String(x).trim()))
			.filter((x) => Number.isFinite(x) && x > 0);
		tokens.forEach((pageNo) => {
			result[Number(pageNo)] = 180;
		});
		return result;
	};
	const parseAggressiveRotationInput = (raw: string): Record<number, number> => {
		const result: Record<number, number> = {};
		const tokens = String(raw || '')
			.split(',')
			.map((x) => String(x).trim())
			.filter(Boolean);
		tokens.forEach((token) => {
			const [pagePart, degPart] = token.split(':').map((x) => String(x).trim());
			const page = Number(pagePart);
			if (!Number.isFinite(page) || page <= 0) {
				return;
			}
			const deg = degPart === undefined || degPart === '' ? 180 : Number(degPart);
			if (!Number.isFinite(deg)) {
				return;
			}
			const normalized = ((Math.trunc(deg) % 360) + 360) % 360;
			if (normalized !== 90 && normalized !== 180 && normalized !== 270) {
				return;
			}
			result[Math.trunc(page)] = normalized;
		});
		return result;
	};
	const statusLabel = (status: string) => {
		const map: Record<string, string> = {
			pending: 'Oczekuje',
			collecting: 'Kompletowanie',
			ready: 'Gotowe',
			confirmed: 'Potwierdzone',
			notified: 'Zaproszenie wysłane',
			active: 'Aktywny dostęp',
			expired: 'Wygasłe',
			invitation_expired: 'Wygasło zaproszenie',
			paper_only: 'Praca papierowa',
			canceled: 'Cofnięte',
		};
		return map[status] || status;
	};
	const fileStatusLabel = (status: string) => {
		const map: Record<string, string> = {
			new: 'Nowy',
			split: 'Rozdzielony',
			assigned: 'Przypisany',
			rejected: 'Odrzucony',
			obsolete: 'Nieaktualny',
			docx_original: 'Oryginał DOCX',
		};
		return map[status] || status;
	};
	const fileCategoryLabel = (category: string) => {
		const map: Record<string, string> = {
			unknown: 'Nieznana',
			review: 'Recenzja',
			combined_review: 'Połączone recenzje',
			reviewed: 'Skan pracy sprawdzonej',
			original: 'Praca oryginalna',
		};
		return map[category] || category;
	};
	const resolvedFileCategoryLabel = (item: any) => {
		if (String(item?.file_status || '') === 'assigned') {
			const doc = documentBySourceIndexId.get(Number(item?.id || 0));
			if (doc?.document_type) {
				return `Przypisany: ${docTypeLabel(String(doc.document_type))}`;
			}
		}
		return fileCategoryLabel(String(item?.file_category || 'unknown'));
	};
	const setCategory = async (item: any, category: string) => {
		await action(
			() => setStage3FileCategory(token, Number(item.id), { category }),
			'Kategoria pliku zaktualizowana.',
			'index',
		);
	};
	const toggleIndexSelection = (id: number, checked: boolean) => {
		setIndexSelectedIds((prev) => ({ ...prev, [id]: checked }));
	};
	const toggleSelectAllFiltered = (checked: boolean) => {
		setIndexSelectedIds((prev) => {
			const next = { ...prev };
			filteredFileIndex.forEach((item) => {
				next[Number(item.id)] = checked;
			});
			return next;
		});
	};
	const runBulkLinkFromIndex = async () => {
		const items = selectedFilteredFileItems;
		if (items.length === 0) {
			onMessage('Najpierw zaznacz rekordy do grupowego przypisania.');
			return;
		}
		const selectedPairId = Number(indexFilters.jury_pair_id || 0);
		const pairSample =
			selectedPairId > 0 ? items.find((row) => Number(row?.jury_pair_id || 0) === selectedPairId) : null;
		const review1Label = String(pairSample?.juror1_name || '').trim()
			? `Recenzja ${String(pairSample?.juror1_name || '').trim()}`
			: 'Recenzja juror 1';
		const review2Label = String(pairSample?.juror2_name || '').trim()
			? `Recenzja ${String(pairSample?.juror2_name || '').trim()}`
			: 'Recenzja juror 2';
		const options: Array<{ no: string; type: string; label: string }> = [
			{ no: '1', type: 'original', label: 'Praca oryginalna' },
			{ no: '2', type: 'reviewed_scan', label: 'Skan pracy sprawdzonej (z komentarzami)' },
		];
		if (selectedPairId > 0) {
			options.push({ no: '3', type: 'review_1', label: review1Label });
			options.push({ no: '4', type: 'review_2', label: review2Label });
		}
		const selected = window.prompt(
			`Wybierz typ przypisania:\n${options.map((o) => `${o.no}. ${o.label}`).join('\n')}`,
			options[0].no,
		);
		if (selected === null) return;
		const picked = options.find((o) => o.no === String(selected).trim());
		if (!picked) {
			onMessage('Nieprawidłowy wybór typu przypisania.');
			return;
		}
		const confirmation = window.confirm(
			`Przypisać ${items.length} zaznaczonych rekordów jako "${picked.label}"?\nPrzypisanie wykona się tylko dla poprawnych kodów i bez duplikatów.`,
		);
		if (!confirmation) return;

		const response = await action(
			() =>
				bulkLinkStage3FileIndex(token, {
					file_index_ids: items.map((row) => Number(row.id)),
					document_type: picked.type,
				}),
			`Uruchomiono grupowe przypisanie (${picked.label}).`,
			'docs+index+completeness',
		);
		const report = response?.data;
		if (report) {
			const linkedCount = Number(report?.linked_count || 0);
			const failedCount = Number(report?.failed_count || 0);
			const failures = Array.isArray(report?.details)
				? report.details.filter((row: any) => !Boolean(row?.ok)).slice(0, 12)
				: [];
			const failureText = failures
				.map((row: any) => `#${row.file_index_id} (${row.work_code || '-'}) - ${row.error || 'Błąd'}`)
				.join('\n');
			window.alert(
				`Wynik grupowego przypisania:\n` +
					`- Wybrane: ${Number(report?.selected_count || items.length)}\n` +
					`- Przypisane: ${linkedCount}\n` +
					`- Problemy: ${failedCount}` +
					(failureText ? `\n\nSzczegóły (max 12):\n${failureText}` : ''),
			);
		}
	};
	const addAccessRequestByLookup = async (channel: 'phone' | 'email') => {
		const query = String(requestLookup || '').trim();
		if (query === '') {
			onMessage('Podaj kod pracy, nazwisko, e-mail lub telefon.');
			return;
		}
		const resolved = await action(() => resolveStage3StudentLookup(token, query), 'Wyszukano ucznia.', 'none');
		const data = resolved?.data;
		if (!data || String(data.status || '') === 'none') {
			onMessage('Nie znaleziono pasującego ucznia na etapie III.');
			return;
		}
		if (String(data.status || '') === 'multiple') {
			const items = Array.isArray(data.items) ? data.items : [];
			const preview = items
				.slice(0, 8)
				.map((row: any) => `${row.student_name || row.student_id} (${row.work_code || '-'})`)
				.join('\n');
			window.alert(
				`Wynik niejednoznaczny (${items.length}). Doprecyzuj wyszukiwanie.\n\nPrzykłady:\n${preview || '(brak podglądu)'}`,
			);
			return;
		}
		const item = data?.item;
		const studentId = Number(item?.student_id || 0);
		if (studentId <= 0) {
			onMessage('Nie udało się ustalić ucznia dla zgłoszenia.');
			return;
		}
		await action(
			() =>
				createStage3AccessRequestAdmin(token, {
					student_id: studentId,
					request_channel: channel,
				}),
			`Dodano zgłoszenie (${channel === 'phone' ? 'telefon' : 'email'}) dla ${item?.student_name || `ucznia #${studentId}`}.`,
			'requests',
		);
	};
	const goToNextInWorkflow = () => {
		if (previewCursor < 0 || previewCursor + 1 >= previewItems.length) return;
		const nextItem = previewItems[previewCursor + 1];
		openPreviewPanel(nextItem, 'preview', previewItems, previewScopeLabel);
	};

	const folderPathLabel = (item: any) => {
		const raw = String(item?.drive_parent_path || '').trim();
		if (raw === '' || raw === item?.drive_file_name) {
			return '(folder główny)';
		}
		return raw;
	};
	const mimeLabel = (item: any) => String(item?.drive_mime_type || '-');
	const supportsHtmlSplit = (item: any): boolean => {
		const mime = String(item?.drive_mime_type || '').toLowerCase();
		// HTML export path works best for Google Docs; DOCX/PDF may fail or be lossy.
		return mime === 'application/vnd.google-apps.document';
	};
	const splitMatchSourceLabel = (source: string) => {
		const map: Record<string, string> = {
			exact: 'Dokładne dopasowanie',
			partial_match: 'Korekta po fragmencie',
			marker_split: 'Podział po markerze',
			potential_format: 'Format potencjalny',
		};
		return map[source] || source || '-';
	};
	const splitAssignModeLabel = (mode: 'review_1' | 'review_2' | 'shared', item: any) => {
		const j1 = String(item?.juror1_name || 'Juror 1').trim() || 'Juror 1';
		const j2 = String(item?.juror2_name || 'Juror 2').trim() || 'Juror 2';
		if (mode === 'review_1') return `${j1} (review_1)`;
		if (mode === 'review_2') return `${j2} (review_2)`;
		return `${j1} + ${j2} (recenzja wspólna)`;
	};
	const normalizeForMatch = (value: string) =>
		value
			.toLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '');
	const suggestSplitAssignMode = (item: any, rows: any[]): '' | 'review_1' | 'review_2' => {
		const sourceText = normalizeForMatch(
			`${item?.drive_file_name || ''} ${String(rows?.[0]?.full_text || '').slice(0, 1600)}`,
		);
		const extractTokens = (name: string) =>
			normalizeForMatch(name)
				.split(/[^a-z0-9]+/i)
				.map((x) => x.trim())
				.filter((x) => x.length >= 3);
		const j1Tokens = extractTokens(String(item?.juror1_name || ''));
		const j2Tokens = extractTokens(String(item?.juror2_name || ''));
		const j1Hit = j1Tokens.some((token) => sourceText.includes(token));
		const j2Hit = j2Tokens.some((token) => sourceText.includes(token));
		if (j1Hit && !j2Hit) return 'review_1';
		if (j2Hit && !j1Hit) return 'review_2';
		return '';
	};
	const isImportReviewCopyName = (value: string) => value.toLowerCase().includes('- import recenzji');
	/** Kopia po imporcie recenzji: sufiks w nazwie albo wiersz „combined_review” utworzony z innego indeksu. */
	const isImportReviewDerivedRow = (item: any) => {
		const name = String(item?.drive_file_name || '').toLowerCase();
		if (name.includes('- import recenzji')) {
			return true;
		}
		if (String(item?.file_category || '') === 'combined_review' && Number(item?.split_from_file_index_id || 0) > 0) {
			return true;
		}
		return false;
	};
	const toggleSplitSegment = (segmentId: number, checked: boolean) => {
		setSplitPreviewSelectedSegmentIds((prev) => {
			if (checked) {
				return prev.includes(segmentId) ? prev : [...prev, segmentId];
			}
			return prev.filter((id) => id !== segmentId);
		});
	};
	const toggleSplitTextExpand = (segmentId: number, expanded: boolean) => {
		setSplitPreviewExpandedSegmentIds((prev) => {
			if (expanded) {
				return prev.includes(segmentId) ? prev : [...prev, segmentId];
			}
			return prev.filter((id) => id !== segmentId);
		});
	};
	const saveSplitJurorPoints = async (row: any, jurorSlot: 'review_1' | 'review_2', forcedPoints?: number | null) => {
		const segmentId = Number(row?.segment_id || 0);
		const workCode = String(row?.work_code || '').trim();
		if (!segmentId || !workCode) {
			onMessage('Brak kodu pracy lub segmentu do zapisu punktów jurora.');
			return;
		}
		const raw =
			forcedPoints !== undefined
				? String(forcedPoints)
				: String(
						jurorSlot === 'review_1'
							? (splitJuror1PointDrafts[segmentId] ?? '')
							: (splitJuror2PointDrafts[segmentId] ?? ''),
					)
						.trim()
						.replace(',', '.');
		const points = raw === '' ? null : Number(raw);
		if (points !== null && (!Number.isFinite(points) || points < 0 || points > thirdAMaxPoints)) {
			onMessage(`Punkty jurora muszą być liczbą z zakresu 0-${thirdAMaxPoints}.`);
			return;
		}
		setSplitJurorPointSavingIds((prev) => (prev.includes(segmentId) ? prev : [...prev, segmentId]));
		try {
			await setStage3JurorPointsByWorkCode(token, {
				work_code: workCode,
				juror_slot: jurorSlot,
				points,
			});
			onMessage(`Zapisano punkty ${jurorSlot} dla kodu ${workCode}.`);
			if (jurorSlot === 'review_1') {
				setSplitJuror1PointDrafts((prev) => ({ ...prev, [segmentId]: points === null ? '' : String(points) }));
			} else {
				setSplitJuror2PointDrafts((prev) => ({ ...prev, [segmentId]: points === null ? '' : String(points) }));
			}
			setSplitPreviewRows((prev) =>
				prev.map((entry: any) =>
					Number(entry?.segment_id || 0) !== segmentId
						? entry
						: {
								...entry,
								db_juror1_points: jurorSlot === 'review_1' ? points : entry.db_juror1_points,
								db_juror2_points: jurorSlot === 'review_2' ? points : entry.db_juror2_points,
							},
				),
			);
		} catch (error: any) {
			onMessage(error?.response?.data?.message || 'Nie udało się zapisać punktów jurora.');
		} finally {
			setSplitJurorPointSavingIds((prev) => prev.filter((id) => id !== segmentId));
		}
	};
	const applyExtractedPointsToSelectedJuror = async (row: any) => {
		if (!row?.splittable) {
			onMessage('Ten fragment nie jest dzielony - brak przypisania do jurora.');
			return;
		}
		const extracted = row?.points_extracted;
		if (extracted === null || extracted === undefined) {
			onMessage('Brak punktów wykrytych w treści recenzji.');
			return;
		}
		if (splitAssignMode !== 'review_1' && splitAssignMode !== 'review_2') {
			onMessage('Wybierz jurora 1 albo 2, aby przepisać punkty.');
			return;
		}
		const ok = window.confirm(
			`Przepisać ${extracted}/${thirdAMaxPoints} do ${splitAssignMode === 'review_1' ? 'Jurora 1' : 'Jurora 2'} dla kodu ${row.work_code}?`,
		);
		if (!ok) return;
		await saveSplitJurorPoints(row, splitAssignMode, Number(extracted));
	};
	const isExtractedInRange = (value: any) => {
		const num = Number(value);
		return Number.isFinite(num) && num >= 0 && num <= thirdAMaxPoints;
	};
	const getSelectedJurorDbPoints = (row: any): number | null => {
		if (splitAssignMode === 'review_1') {
			return row?.db_juror1_points !== null && row?.db_juror1_points !== undefined
				? Number(row.db_juror1_points)
				: null;
		}
		if (splitAssignMode === 'review_2') {
			return row?.db_juror2_points !== null && row?.db_juror2_points !== undefined
				? Number(row.db_juror2_points)
				: null;
		}
		return null;
	};
	const isExtractedMismatchForSelectedJuror = (row: any): boolean => {
		if (!row?.splittable) return false;
		if (splitAssignMode !== 'review_1' && splitAssignMode !== 'review_2') return false;
		if (!isExtractedInRange(row?.points_extracted)) return false;
		const extracted = Number(row.points_extracted);
		const current = getSelectedJurorDbPoints(row);
		return current === null || Number(current) !== extracted;
	};
	const canBulkApplyExtractedPoints = () => {
		if (!splitAutoAssign) return false;
		if (splitAssignMode !== 'review_1' && splitAssignMode !== 'review_2') return false;
		const splittableRows = (splitPreviewRows || []).filter((row: any) => Boolean(row?.splittable));
		if (splittableRows.length === 0) return false;
		return splittableRows.every((row: any) => isExtractedInRange(row?.points_extracted));
	};
	const applyAllExtractedPointsToSelectedJuror = async () => {
		if (!canBulkApplyExtractedPoints()) {
			onMessage('Nie można przepisać wszystkich punktów: sprawdź jurora, auto-przypisanie i komplet punktów.');
			return;
		}
		const mismatches = (splitPreviewRows || []).filter((row: any) => isExtractedMismatchForSelectedJuror(row));
		if (mismatches.length === 0) {
			onMessage('Brak rozbieżności punktów do przepisania.');
			return;
		}
		const ok = window.confirm(
			`Przepisać punkty z recenzji dla ${mismatches.length} pozycji do ${splitAssignMode === 'review_1' ? 'Jurora 1' : 'Jurora 2'}?`,
		);
		if (!ok) return;
		for (const row of mismatches) {
			await saveSplitJurorPoints(row, splitAssignMode as 'review_1' | 'review_2', Number(row.points_extracted));
		}
		onMessage(`Przepisano punkty dla ${mismatches.length} pozycji.`);
	};
	const openSplitPreview = async (item: any, resetOptions: boolean = false) => {
		const effectiveRemovePoints = resetOptions ? false : splitRemovePoints;
		const effectiveRemoveWorkCode = resetOptions ? false : splitRemoveWorkCode;
		const effectiveStripTermsRaw = resetOptions ? '' : splitStripTermsRaw;
		setSplitPreviewItem(item);
		setSplitPreviewRows([]);
		setSplitPreviewError('');
		setSplitPreviewAlerts([]);
		setSplitPreviewCorrections([]);
		setSplitPreviewForeignCodes([]);
		setSplitPreviewPotentialCodes([]);
		setSplitPreviewSourceFileIndexId(null);
		setSplitPreviewSourceDriveFileId('');
		setSplitPreviewSourceDriveFileName('');
		setSplitPreviewSourceDriveMimeType('');
		setSplitPreviewSelectedSegmentIds([]);
		setSplitPreviewExpandedSegmentIds([]);
		setSplitPreviewHtml('');
		setSplitPreviewResultItems([]);
		setSplitPreviewResultAssignments([]);
		if (resetOptions) {
			setSplitRemovePoints(false);
			setSplitRemoveWorkCode(false);
			setSplitStripTermsRaw('');
			setSplitPreviewShowSanitized(false);
		}
		setSplitPreviewOpen(true);
		setSplitPreviewBusy('analyzing');
		setLoading(true);
		try {
			const splitTerms = effectiveStripTermsRaw
				.split(/[;\n]+/)
				.map((term) => term.trim())
				.filter((term) => term.length > 0);
			const response = await getStage3SplitPreview(token, Number(item.id), {
				remove_points: effectiveRemovePoints,
				remove_work_code: effectiveRemoveWorkCode,
				strip_terms: splitTerms,
			});
			const rows = response?.data?.items || [];
			setSplitPreviewRows(rows);
			setSplitAssignMode(suggestSplitAssignMode(item, rows));
			const juror1Drafts: Record<number, string> = {};
			const juror2Drafts: Record<number, string> = {};
			rows.forEach((row: any) => {
				const id = Number(row?.segment_id || 0);
				if (id > 0) {
					juror1Drafts[id] =
						row?.db_juror1_points !== null && row?.db_juror1_points !== undefined && row?.db_juror1_points !== ''
							? String(row.db_juror1_points)
							: '';
					juror2Drafts[id] =
						row?.db_juror2_points !== null && row?.db_juror2_points !== undefined && row?.db_juror2_points !== ''
							? String(row.db_juror2_points)
							: '';
				}
			});
			setSplitJuror1PointDrafts(juror1Drafts);
			setSplitJuror2PointDrafts(juror2Drafts);
			setSplitPreviewAlerts(response?.data?.alerts || []);
			setSplitPreviewCorrections(response?.data?.corrections || []);
			setSplitPreviewForeignCodes(response?.data?.foreign_commission_codes || []);
			setSplitPreviewPotentialCodes(response?.data?.potential_codes || []);
			setSplitPreviewSelectedSegmentIds(
				rows
					.filter((row: any) => Boolean(row?.splittable) && Boolean(row?.default_selected))
					.map((row: any) => Number(row?.segment_id || 0))
					.filter((id: number) => id > 0),
			);
			setSplitPreviewSourceFileIndexId(
				Number.isFinite(Number(response?.data?.source_file_index_id))
					? Number(response?.data?.source_file_index_id)
					: null,
			);
			setSplitPreviewSourceDriveFileId(String(response?.data?.source_drive_file_id || ''));
			setSplitPreviewSourceDriveFileName(String(response?.data?.source_drive_file_name || ''));
			setSplitPreviewSourceDriveMimeType(String(response?.data?.source_drive_mime_type || ''));
			setSplitPreviewHtml(String(response?.data?.html || ''));
		} catch (error: any) {
			const message = error?.response?.data?.message || 'Nie udało się pobrać podglądu splitu.';
			setSplitPreviewError(message);
			onMessage(message);
		} finally {
			setSplitPreviewBusy('idle');
			setLoading(false);
		}
	};
	const runSplitNow = async () => {
		if (!splitPreviewItem || !splitPreviewSourceFileIndexId) return;
		setSplitPreviewBusy('splitting');
		setLoading(true);
		try {
			const response = await splitStage3CombinedReview(
				token,
				Number(splitPreviewSourceFileIndexId),
				Number(splitPreviewItem.jury_pair_id || 0),
				{
					auto_assign: splitAutoAssign,
					assign_mode: splitAssignMode === '' ? undefined : splitAssignMode,
					selected_segment_ids: splitPreviewSelectedSegmentIds,
					remove_points: splitRemovePoints,
					remove_work_code: splitRemoveWorkCode,
					strip_terms: splitStripTermsRaw
						.split(/[;\n]+/)
						.map((term) => term.trim())
						.filter((term) => term.length > 0),
				},
			);
			setSplitPreviewResultItems(response?.data?.items || response?.data?.created || []);
			setSplitPreviewResultAssignments(response?.data?.assignments || []);
			onMessage('Rozdzielono recenzje.');
			setSplitPreviewOpen(false);
			setSplitSummaryOpen(true);
			loadAll();
		} catch (error: any) {
			const message = error?.response?.data?.message || 'Nie udało się rozdzielić recenzji.';
			onMessage(message);
		} finally {
			setSplitPreviewBusy('idle');
			setLoading(false);
		}
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth='xl'
				fullWidth
			>
				<DialogTitle>Udostępnianie sprawdzonych prac (Etap IIIA)</DialogTitle>
				<DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '80vh', overflow: 'hidden' }}>
					<Stack
						direction='row'
						justifyContent='space-between'
						alignItems='center'
						mb={2}
					>
						<Tabs
							value={tab}
							onChange={(_, next) => setTab(next)}
						>
							<Tab label='Indeks plików' />
							<Tab label='Dokumenty' />
							<Tab label='Kompletność' />
							<Tab label='Zgłoszenia' />
							<Tab label='Ustawienia' />
						</Tabs>
						<Button
							variant='outlined'
							onClick={loadAll}
							disabled={loading}
						>
							Odśwież
						</Button>
					</Stack>

					{tab === 0 && (
						<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
							{showIndexCategoryHint && (
								<Alert
									severity='info'
									sx={{ mb: 2 }}
									action={
										<IconButton
											size='small'
											onClick={() => setShowIndexCategoryHint(false)}
										>
											<CloseIcon fontSize='small' />
										</IconButton>
									}
								>
									Reguły auto-kategoryzacji: nazwa/ścieżka z `recenz/review` → `review`; `zbior/lacz/combined` →
									`combined_review`; `skan/sprawdz` → `reviewed`; `oryg/original` → `original`; reszta → `unknown`.
								</Alert>
							)}
							{showIndexAssignmentHint && (
								<Alert
									severity='warning'
									sx={{ mb: 2 }}
									action={
										<IconButton
											size='small'
											onClick={() => setShowIndexAssignmentHint(false)}
										>
											<CloseIcon fontSize='small' />
										</IconButton>
									}
								>
									`Oznacz przypisany` ustawia tylko status inwentarza pliku. Faktyczne podpięcie do ucznia wykonujesz
									przez `Przypisz w Dokumentach`.
								</Alert>
							)}
							<Stack
								direction='row'
								gap={1}
								mb={1}
								flexWrap='wrap'
								alignItems='center'
							>
								<Tooltip title='Wczytaj dane z Google Drive do indeksu plików'>
									<span>
										<Button
											variant='contained'
											size='small'
											disabled={loading}
											onClick={() => action(() => scanStage3FileIndex(token), 'Wczytano indeks plików z Drive.')}
										>
											WCZYTAJ
										</Button>
									</span>
								</Tooltip>
								<Tooltip title='Sprawdź spójność powiązań indeksu z dokumentami uczniów'>
									<span>
										<Button
											variant='outlined'
											size='small'
											disabled={loading}
											onClick={() =>
												action(() => verifyStage3DocumentLinks(token), 'Zweryfikowano powiązania dokumentów.')
											}
										>
											SPRAWDŹ
										</Button>
									</span>
								</Tooltip>
								<Tooltip title='Widok kompaktowy tabeli'>
									<IconButton
										size='small'
										color={compactIndexView ? 'primary' : 'default'}
										onClick={() => setIndexViewPreset('compact')}
									>
										<ViewCompactAltIcon fontSize='small' />
									</IconButton>
								</Tooltip>
								<Tooltip title='Widok standardowy tabeli'>
									<IconButton
										size='small'
										color={!compactIndexView ? 'primary' : 'default'}
										onClick={() => setIndexViewPreset('standard')}
									>
										<TableRowsIcon fontSize='small' />
									</IconButton>
								</Tooltip>
								<FormControl
									size='small'
									sx={{ minWidth: 190 }}
								>
									<InputLabel>Komisja</InputLabel>
									<Select
										label='Komisja'
										value={indexFilters.jury_pair_id}
										onChange={(e) =>
											setIndexFilters((prev) => ({ ...prev, jury_pair_id: String(e.target.value || '') }))
										}
									>
										<MenuItem value=''>Wszystkie komisje</MenuItem>
										{juryPairOptions.map((pair) => (
											<MenuItem
												key={pair.id}
												value={String(pair.id)}
											>
												{pair.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl
									size='small'
									sx={{ minWidth: 220 }}
								>
									<InputLabel>Status</InputLabel>
									<Select
										multiple
										label='Status'
										value={indexFilters.statuses}
										renderValue={(selected) => {
											const values = Array.isArray(selected) ? selected : [];
											if (values.length === 0) {
												return 'Wszystkie';
											}
											return values.map((v) => fileStatusLabel(String(v))).join(', ');
										}}
										onChange={(e) =>
											setIndexFilters((prev) => ({
												...prev,
												statuses: (e.target.value as string[]) || [],
											}))
										}
									>
										{statusOptions.map((status) => (
											<MenuItem
												key={status}
												value={status}
											>
												<Checkbox
													size='small'
													checked={indexFilters.statuses.includes(status)}
												/>
												<ListItemText primary={fileStatusLabel(status)} />
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<TextField
									size='small'
									label='Szukaj (plik/ścieżka/kod)'
									value={indexFilters.query}
									onChange={(e) => setIndexFilters((prev) => ({ ...prev, query: String(e.target.value || '') }))}
									sx={{ minWidth: 280 }}
								/>
							</Stack>
							<Typography
								variant='caption'
								sx={{ color: '#666', mb: 1 }}
							>
								Liczba rekordów: {filteredFileIndex.length}
							</Typography>
							<Box sx={{ minHeight: 0, flex: 1, overflow: 'auto', border: '1px solid #e5e5e5', borderRadius: 1 }}>
								<Table
									size='small'
									stickyHeader
								>
									<TableHead>
										<TableRow>
											<TableCell>ID</TableCell>
											<TableCell>Plik</TableCell>
											{!compactIndexView && <TableCell>Komisja</TableCell>}
											{!compactIndexView && <TableCell>Ścieżka</TableCell>}
											<TableCell padding='checkbox'>
												<Checkbox
													size='small'
													checked={allFilteredSelected}
													indeterminate={selectedFilteredFileItems.length > 0 && !allFilteredSelected}
													onChange={(e) => toggleSelectAllFiltered(Boolean(e.target.checked))}
												/>
											</TableCell>
											<TableCell>Kod pracy</TableCell>
											<TableCell>Kategoria</TableCell>
											<TableCell>Status</TableCell>
											<TableCell>
												<Stack
													direction='row'
													alignItems='center'
													gap={1}
												>
													<Typography variant='body2'>Akcje</Typography>
													<Tooltip title='Grupowe przypisanie zaznaczonych rekordów'>
														<IconButton
															size='small'
															onClick={runBulkLinkFromIndex}
															disabled={loading || selectedFilteredFileItems.length === 0}
														>
															<LinkIcon fontSize='small' />
														</IconButton>
													</Tooltip>
												</Stack>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{filteredFileIndex.map((item) => (
											<TableRow key={item.id}>
												<TableCell>{item.id}</TableCell>
												<TableCell>
													<Typography variant='body2'>{item.drive_file_name}</Typography>
													{!compactIndexView && (
														<Typography
															variant='caption'
															sx={{ color: '#666' }}
														>
															MIME: {mimeLabel(item)}
														</Typography>
													)}
												</TableCell>
												{!compactIndexView && <TableCell>{item.jury_pair_name || '-'}</TableCell>}
												{!compactIndexView && <TableCell>{folderPathLabel(item)}</TableCell>}
												<TableCell padding='checkbox'>
													<Checkbox
														size='small'
														checked={Boolean(indexSelectedIds[Number(item.id)])}
														onChange={(e) => toggleIndexSelection(Number(item.id), Boolean(e.target.checked))}
													/>
												</TableCell>
												<TableCell>{item.matched_work_code || '-'}</TableCell>
												<TableCell sx={{ whiteSpace: 'nowrap' }}>
													<FormControl
														size='small'
														sx={{ minWidth: 132 }}
													>
														<Select
															value={String(item?.file_category || 'unknown')}
															onChange={(e) => setCategory(item, String(e.target.value || 'unknown'))}
															sx={{ fontSize: 12, height: 32 }}
														>
															{categoryOptions.map((category) => (
																<MenuItem
																	key={category}
																	value={category}
																	sx={{ fontSize: 12, minHeight: 28 }}
																>
																	{fileCategoryLabel(category)}
																</MenuItem>
															))}
														</Select>
													</FormControl>
													{String(item?.file_status || '') === 'assigned' && (
														<Typography
															variant='caption'
															sx={{ display: 'block', color: '#666', mt: 0.5 }}
														>
															{resolvedFileCategoryLabel(item)}
														</Typography>
													)}
												</TableCell>
												<TableCell>
													<Tooltip
														title={
															String(item?.file_status || '') === 'docx_original' && item?.replaced_by_file_id
																? 'Ten wpis to oryginalny DOCX zastąpiony dokumentem Google przy przypisaniu (otwórz podgląd w wierszu z nowym plikiem).'
																: ''
														}
													>
														<span>
															<Chip
																size='small'
																color={
																	item.file_status === 'assigned'
																		? 'success'
																		: item.file_status === 'docx_original'
																			? 'warning'
																			: 'default'
																}
																label={fileStatusLabel(String(item.file_status || ''))}
															/>
														</span>
													</Tooltip>
												</TableCell>
												<TableCell sx={{ py: 0.5 }}>
													<Stack
														direction='row'
														gap={0.25}
														flexWrap='nowrap'
														sx={{ '& .MuiIconButton-root': { p: 0.25 } }}
													>
														<Tooltip title='Podgląd'>
															<IconButton
																size='small'
																onClick={() => openPreviewPanel(item, 'preview')}
															>
																<PreviewIcon fontSize='small' />
															</IconButton>
														</Tooltip>
														<Tooltip title={getDriveEditTooltipForIndexItem(item)}>
															<IconButton
																size='small'
																onClick={() => {
																	const url = getDriveEditUrlForIndexItem(item);
																	if (url) {
																		window.open(url, '_blank', 'noopener,noreferrer');
																	}
																}}
															>
																<EditIcon fontSize='small' />
															</IconButton>
														</Tooltip>
														<Tooltip title='Kopia agresywna (obrót stron 90/180/270, bez przypięcia do dokumentu)'>
															<IconButton
																size='small'
																onClick={() => createAggressiveRotatedCopyFromIndex(item)}
															>
																<RotateRightIcon fontSize='small' />
															</IconButton>
														</Tooltip>
														<Tooltip title='Przypisz w Dokumentach'>
															<IconButton
																size='small'
																onClick={() => fillLinkByCode(item)}
															>
																<LinkIcon fontSize='small' />
															</IconButton>
														</Tooltip>
														<Tooltip title='Oznacz przypisany'>
															<IconButton
																size='small'
																onClick={() =>
																	action(
																		() => setStage3FileStatus(token, item.id, { status: 'assigned' }),
																		'Status zaktualizowany.',
																		'index',
																	)
																}
															>
																<AssignmentTurnedInIcon fontSize='small' />
															</IconButton>
														</Tooltip>
														<Tooltip title='Odrzuć'>
															<IconButton
																size='small'
																onClick={() =>
																	action(
																		() => setStage3FileStatus(token, item.id, { status: 'rejected' }),
																		'Plik odrzucony.',
																		'index',
																	)
																}
															>
																<BlockIcon fontSize='small' />
															</IconButton>
														</Tooltip>
														{item.file_category === 'combined_review' && (
															<Tooltip title='Rozdziel recenzje (podgląd i wykonanie w jednym oknie)'>
																<IconButton
																	size='small'
																	onClick={() => openSplitPreview(item, true)}
																>
																	<CallSplitIcon fontSize='small' />
																</IconButton>
															</Tooltip>
														)}
													</Stack>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
							{selectedCommentsFile !== '' && (
								<Box
									mt={2}
									sx={{ maxHeight: 260, overflow: 'auto', border: '1px solid #e5e5e5', borderRadius: 1, p: 1 }}
								>
									<Typography
										variant='subtitle2'
										sx={{ mb: 1 }}
									>
										Komentarze / adnotacje: {selectedCommentsFile}
									</Typography>
									{selectedCommentsMeta && (
										<Typography
											variant='caption'
											sx={{ display: 'block', mb: 1, color: '#666' }}
										>
											Źródło: {selectedCommentsMeta.source || '-'} | HTTP: {selectedCommentsMeta.http_status || '-'} |
											API: {selectedCommentsMeta.api_comments_count ?? '-'} | Wynik:{' '}
											{selectedCommentsMeta.items_count ?? selectedComments.length}{' '}
											{selectedCommentsMeta.error ? `| Błąd: ${selectedCommentsMeta.error}` : ''}
										</Typography>
									)}
									{selectedComments.length === 0 ? (
										<Alert severity='info'>Brak komentarzy/adnotacji dla tego pliku.</Alert>
									) : (
										<Table
											size='small'
											stickyHeader
										>
											<TableHead>
												<TableRow>
													<TableCell>#</TableCell>
													<TableCell>Fragment / strona</TableCell>
													<TableCell>Treść</TableCell>
													<TableCell>Autor</TableCell>
													<TableCell>Typ</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{selectedComments.map((row: any, idx: number) => (
													<TableRow key={`${idx}-${row.no || ''}-${row.page || ''}`}>
														<TableCell>{row.no || idx + 1}</TableCell>
														<TableCell>{row.quoted || (row.page ? `Strona ${row.page}` : '-')}</TableCell>
														<TableCell>{row.content || row.htmlContent || '-'}</TableCell>
														<TableCell>{row.author || '-'}</TableCell>
														<TableCell>{row.type || 'comment'}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									)}
								</Box>
							)}
						</Box>
					)}

					{tab === 1 && (
						<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
							<Alert
								severity='info'
								sx={{ mb: 2 }}
							>
								Komplet wymaga: skanu pracy sprawdzonej oraz recenzji obu jurorów (lub recenzji wspólnej).
							</Alert>
							<Alert
								severity='info'
								sx={{ mb: 2 }}
							>
								Najwygodniej: w INDEX kliknij „Przypisz w Dokumentach” (wypełni kod pracy i plik), tutaj kod pracy
								zdekoduje się do ucznia automatycznie.
							</Alert>
							<Stack
								direction='row'
								gap={1}
								flexWrap='wrap'
								mb={2}
							>
								<TextField
									label='ID ucznia (uzupełniane po kodzie)'
									size='small'
									value={newLink.student_id}
									onChange={(e) => setNewLink((prev) => ({ ...prev, student_id: e.target.value }))}
								/>
								<TextField
									label='Kod pracy'
									size='small'
									value={newLink.work_code}
									onChange={(e) => setNewLink((prev) => ({ ...prev, work_code: e.target.value }))}
									onBlur={(e) => {
										resolveAndFillStudent(String(e.target.value || ''));
									}}
								/>
								<FormControl
									size='small'
									sx={{ minWidth: 160 }}
								>
									<InputLabel>Typ dokumentu</InputLabel>
									<Select
										label='Typ dokumentu'
										value={newLink.document_type}
										onChange={(e) => setNewLink((prev) => ({ ...prev, document_type: e.target.value }))}
									>
										<MenuItem value='original'>Praca oryginalna</MenuItem>
										<MenuItem value='reviewed_scan'>Skan pracy sprawdzonej</MenuItem>
										<MenuItem value='review_1'>Recenzja jurora 1</MenuItem>
										<MenuItem value='review_2'>Recenzja jurora 2</MenuItem>
										<MenuItem value='extra'>Dokument dodatkowy</MenuItem>
									</Select>
								</FormControl>
								<FormControl
									size='small'
									sx={{ minWidth: 340 }}
								>
									<InputLabel>Plik z indeksu</InputLabel>
									<Select
										label='Plik z indeksu'
										value={newLink.file_index_id}
										onChange={(e) => {
											const value = String(e.target.value || '');
											const id = Number(value || 0);
											const item = id > 0 ? fileIndexById.get(id) : null;
											setNewLink((prev) => ({
												...prev,
												file_index_id: value,
												drive_file_id: item?.drive_file_id || '',
												work_code: item?.matched_work_code || prev.work_code,
											}));
										}}
									>
										<MenuItem value=''>-- wybierz --</MenuItem>
										{fileIndex.map((item) => (
											<MenuItem
												key={item.id}
												value={String(item.id)}
											>
												{item.drive_file_name} | {item.jury_pair_name || '-'} | kod: {item.matched_work_code || '-'}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<Button
									variant={manualLinkMode ? 'contained' : 'outlined'}
									onClick={() => setManualLinkMode((prev) => !prev)}
								>
									Tryb ręczny
								</Button>
								{manualLinkMode && (
									<TextField
										label='drive_file_id (manual)'
										size='small'
										value={newLink.drive_file_id}
										onChange={(e) =>
											setNewLink((prev) => ({ ...prev, drive_file_id: e.target.value, file_index_id: '' }))
										}
									/>
								)}
								<TextField
									label='Zakres stron'
									size='small'
									value={newLink.page_range}
									onChange={(e) => setNewLink((prev) => ({ ...prev, page_range: e.target.value }))}
								/>
								<Button
									variant='text'
									onClick={() =>
										setNewLink({
											student_id: '',
											work_code: '',
											document_type: 'review_1',
											file_index_id: '',
											drive_file_id: '',
											page_range: '',
										})
									}
								>
									Wyczyść
								</Button>
								<Button
									variant='contained'
									disabled={loading || (!newLink.student_id && !newLink.work_code)}
									onClick={() =>
										action(
											async () => {
												let studentId = Number(newLink.student_id);
												if (!studentId && newLink.work_code) {
													studentId = (await resolveAndFillStudent(newLink.work_code)) || 0;
												}
												if (!studentId) {
													throw new Error('Nie udało się ustalić ucznia na podstawie kodu pracy.');
												}
												return linkStage3Document(token, {
													student_id: studentId,
													document_type: newLink.document_type,
													file_index_id:
														!manualLinkMode && newLink.file_index_id ? Number(newLink.file_index_id) : undefined,
													drive_file_id: manualLinkMode ? newLink.drive_file_id || undefined : undefined,
													page_range: newLink.page_range || undefined,
												});
											},
											'Powiązano dokument.',
											'docs+index+completeness',
										)
									}
								>
									Powiąż dokument
								</Button>
							</Stack>
							<Box sx={{ minHeight: 0, flex: 1, overflow: 'auto', border: '1px solid #e5e5e5', borderRadius: 1 }}>
								<Table
									size='small'
									stickyHeader
								>
									<TableHead>
										<TableRow>
											<TableCell>ID</TableCell>
											<TableCell>Uczeń</TableCell>
											<TableCell>Typ</TableCell>
											<TableCell>Plik</TableCell>
											<TableCell>Komisja</TableCell>
											<TableCell>Akcje</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{documents.map((row) => (
											<TableRow key={row.id}>
												<TableCell>{row.id}</TableCell>
												<TableCell>{row.student_name || row.student_id}</TableCell>
												<TableCell>{docTypeLabel(row.document_type)}</TableCell>
												<TableCell>{row.drive_file_name}</TableCell>
												<TableCell>{row.source_jury_pair_id || '-'}</TableCell>
												<TableCell>
													<Stack
														direction='row'
														gap={1}
													>
														<Button
															size='small'
															onClick={() =>
																window.open(getDriveViewUrl(row.drive_file_id), '_blank', 'noopener,noreferrer')
															}
														>
															Przeglądaj
														</Button>
														<Button
															size='small'
															onClick={() => editDocumentAssignment(row)}
														>
															Edytuj przypisanie
														</Button>
														<Button
															size='small'
															color='error'
															onClick={() =>
																action(() => deleteStage3Document(token, row.id), 'Usunięto powiązanie dokumentu.')
															}
														>
															Usuń
														</Button>
													</Stack>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</Box>
					)}

					{tab === 2 && (
						<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
							<Stack
								direction='row'
								gap={1}
								mb={1}
								flexWrap='wrap'
								alignItems='center'
							>
								<FormControl
									size='small'
									sx={{ minWidth: 220 }}
								>
									<InputLabel>Komisje</InputLabel>
									<Select
										label='Komisje'
										value={completenessFilters.jury_pair_id}
										onChange={(e) =>
											setCompletenessFilters((prev) => ({ ...prev, jury_pair_id: String(e.target.value || '') }))
										}
									>
										<MenuItem value=''>Wszystkie</MenuItem>
										{juryPairOptions.map((pair) => (
											<MenuItem
												key={pair.id}
												value={String(pair.id)}
											>
												{pair.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<TextField
									size='small'
									label='Szukaj (uczeń, kod pracy)'
									value={completenessFilters.query}
									onChange={(e) => setCompletenessFilters((prev) => ({ ...prev, query: String(e.target.value || '') }))}
									sx={{ minWidth: 260 }}
								/>
								<FormControl
									size='small'
									sx={{ minWidth: 170 }}
								>
									<InputLabel>Prośby</InputLabel>
									<Select
										label='Prośby'
										value={completenessFilters.requests}
										onChange={(e) =>
											setCompletenessFilters((prev) => ({
												...prev,
												requests: String(e.target.value || 'all') as 'all' | 'awaiting' | 'resolved',
											}))
										}
									>
										<MenuItem value='awaiting'>Oczekujące</MenuItem>
										<MenuItem value='resolved'>Zrealizowane</MenuItem>
										<MenuItem value='all'>Wszystkie</MenuItem>
									</Select>
								</FormControl>
								<FormControl
									size='small'
									sx={{ minWidth: 170 }}
								>
									<InputLabel>Weryfikacja</InputLabel>
									<Select
										label='Weryfikacja'
										value={completenessFilters.verification}
										onChange={(e) =>
											setCompletenessFilters((prev) => ({
												...prev,
												verification: String(e.target.value || 'all') as 'all' | 'none' | 'pending' | 'verified',
											}))
										}
									>
										<MenuItem value='none'>Brak</MenuItem>
										<MenuItem value='pending'>Oczekująca</MenuItem>
										<MenuItem value='verified'>Potwierdzona</MenuItem>
										<MenuItem value='all'>Wszystkie</MenuItem>
									</Select>
								</FormControl>
								<FormControl
									size='small'
									sx={{ minWidth: 170 }}
								>
									<InputLabel>Kompletność</InputLabel>
									<Select
										label='Kompletność'
										value={completenessFilters.completeness}
										onChange={(e) =>
											setCompletenessFilters((prev) => ({
												...prev,
												completeness: String(e.target.value || 'all') as 'all' | 'yes' | 'missing',
											}))
										}
									>
										<MenuItem value='all'>Wszystkie</MenuItem>
										<MenuItem value='yes'>TAK</MenuItem>
										<MenuItem value='missing'>Brak</MenuItem>
									</Select>
								</FormControl>
								<Typography
									variant='caption'
									sx={{ color: '#666', ml: 1 }}
								>
									Liczba rekordów: {filteredCompleteness.length}
								</Typography>
							</Stack>
							<Box sx={{ minHeight: 0, flex: 1, overflow: 'auto', border: '1px solid #e5e5e5', borderRadius: 1 }}>
								<Table
									size='small'
									stickyHeader
								>
									<TableHead>
										<TableRow>
											<TableCell>Komisja</TableCell>
											<TableCell>Uczeń</TableCell>
											<TableCell>Kod</TableCell>
											<TableCell>Prośba o wgląd</TableCell>
											<TableCell>Weryfikacja biura</TableCell>
											<TableCell>Auto udostępnienie</TableCell>
											<TableCell>Oryginał pracy</TableCell>
											<TableCell>Skan pracy</TableCell>
											<TableCell>Recenzja jurora 1</TableCell>
											<TableCell>Recenzja jurora 2</TableCell>
											<TableCell>Recenzja wspólna</TableCell>
											<TableCell>Punkty (J1/J2/suma)</TableCell>
											<TableCell>Komplet</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{filteredCompleteness.map((row) => (
											<TableRow
												key={row.student_id}
												sx={
													row.request_needs_manual_verification
														? { backgroundColor: 'rgba(255, 193, 7, 0.12)' }
														: undefined
												}
											>
												<TableCell>{row.assigned_jury_pair_name || '-'}</TableCell>
												<TableCell>{row.student_name}</TableCell>
												<TableCell>{row.work_code || '-'}</TableCell>
												<TableCell>
													{row.has_access_request ? (
														<Chip
															size='small'
															color={row.request_needs_manual_verification ? 'warning' : 'default'}
															label={statusLabel(String(row.request_status || 'pending'))}
														/>
													) : (
														'—'
													)}
												</TableCell>
												<TableCell>
													<Button
														size='small'
														variant={row.access_verified ? 'contained' : 'outlined'}
														color={row.request_needs_manual_verification ? 'warning' : 'primary'}
														onClick={() =>
															action(
																() =>
																	setStage3CompletenessVerified(token, Number(row.student_id), !row.access_verified),
																`Ustawiono weryfikację biura: ${!row.access_verified ? 'TAK' : 'NIE'}.`,
																'completeness+requests',
															)
														}
													>
														{row.access_verified ? 'POTWIERDZONE' : 'NIEPOTWIERDZONE'}
													</Button>
												</TableCell>
												<TableCell>
													<Button
														size='small'
														variant={row.auto_access_disabled ? 'contained' : 'outlined'}
														color={row.auto_access_disabled ? 'warning' : 'inherit'}
														onClick={() =>
															action(
																() =>
																	setStage3CompletenessAutoAccess(
																		token,
																		Number(row.student_id),
																		!row.auto_access_disabled,
																	),
																`Ustawiono wycofanie automatycznej kwalifikacji: ${
																	!row.auto_access_disabled ? 'TAK' : 'NIE'
																}.`,
																'completeness+requests',
															)
														}
													>
														{row.auto_access_disabled ? 'WYCOFANE' : 'AKTYWNE'}
													</Button>
												</TableCell>
												<TableCell>{renderCompletenessDocCell(row, 'original')}</TableCell>
												<TableCell>{renderCompletenessDocCell(row, 'reviewed_scan')}</TableCell>
												<TableCell>{renderCompletenessDocCell(row, 'review_1')}</TableCell>
												<TableCell>{renderCompletenessDocCell(row, 'review_2')}</TableCell>
												<TableCell>
													<Button
														size='small'
														variant={row.shared_review ? 'contained' : 'outlined'}
														onClick={() =>
															action(
																() => setStage3SharedReview(token, Number(row.student_id), !row.shared_review),
																`Ustawiono recenzję wspólną: ${!row.shared_review ? 'TAK' : 'NIE'}.`,
																'completeness',
															)
														}
													>
														{row.shared_review ? 'TAK' : 'NIE'}
													</Button>
												</TableCell>
												<TableCell>
													{row.third_a_juror1_points ?? '-'} / {row.third_a_juror2_points ?? '-'} /{' '}
													{row.third_a_stage_points ?? '-'}
												</TableCell>
												<TableCell>{row.is_complete ? 'TAK' : 'NIE'}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</Box>
					)}

					{tab === 3 && (
						<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
							<Alert
								severity='info'
								sx={{ mb: 1.5 }}
							>
								<strong>Wyślij zaproszenie</strong> — wysyła uczniowi informację, że może otworzyć dostęp.
								<br />
								<strong>Nadaj dostęp</strong> — nadaje uprawnienia do plików na czas określony w ustawieniach.
							</Alert>
							<Stack
								direction='row'
								gap={1}
								mb={2}
								flexWrap='wrap'
							>
								<Button
									variant='outlined'
									disabled={loading}
									onClick={() =>
										action(() => expireStage3Invitations(token), 'Zaktualizowano wygasłe zaproszenia.', 'requests')
									}
								>
									Odśwież wygasłe zaproszenia
								</Button>
								<TextField
									size='small'
									label='Dodaj zgłoszenie: kod/nazwisko/e-mail/telefon'
									value={requestLookup}
									onChange={(e) => setRequestLookup(String(e.target.value || ''))}
									sx={{ minWidth: 340 }}
								/>
								<Button
									variant='outlined'
									disabled={loading}
									onClick={() => addAccessRequestByLookup('phone')}
								>
									DODAJ ZGŁOSZENIE TELEFON
								</Button>
								<Button
									variant='outlined'
									disabled={loading}
									onClick={() => addAccessRequestByLookup('email')}
								>
									DODAJ ZGŁOSZENIE EMAIL
								</Button>
							</Stack>
							<Box sx={{ minHeight: 0, flex: 1, overflow: 'auto', border: '1px solid #e5e5e5', borderRadius: 1 }}>
								<Table
									size='small'
									stickyHeader
								>
									<TableHead>
										<TableRow>
											<TableCell>ID</TableCell>
											<TableCell>Uczeń</TableCell>
											<TableCell>Status</TableCell>
											<TableCell>Data utworzenia</TableCell>
											<TableCell>Data statusu</TableCell>
											<TableCell>Kanał</TableCell>
											<TableCell>Akcje</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{visibleRequests.map((row) => (
											<TableRow key={row.id}>
												<TableCell>{row.id}</TableCell>
												<TableCell>{row.student_name || row.student_id}</TableCell>
												<TableCell>{statusLabel(String(row.status_ui || row.status || ''))}</TableCell>
												<TableCell>{row.created_at || '-'}</TableCell>
												<TableCell>{row.status_at || '-'}</TableCell>
												<TableCell>{row.request_channel}</TableCell>
												<TableCell>
													<Stack
														direction='row'
														gap={1}
														flexWrap='wrap'
													>
														<Button
															size='small'
															disabled={!(row.status === 'ready')}
															onClick={() =>
																action(
																	() => confirmStage3AccessRequest(token, row.id),
																	'Potwierdzono zgłoszenie.',
																	'requests',
																)
															}
														>
															Potwierdź
														</Button>
														<Button
															size='small'
															disabled={
																!(
																	row.status === 'confirmed' ||
																	row.status === 'ready' ||
																	row.status === 'invitation_expired'
																)
															}
															onClick={() =>
																action(() => sendStage3Invitation(token, row.id), 'Wysłano zaproszenie.', 'requests')
															}
														>
															Wyślij zaproszenie
														</Button>
														<Button
															size='small'
															onClick={() =>
																action(() => grantStage3Access(token, row.id), 'Nadano dostęp.', 'requests')
															}
														>
															Nadaj dostęp
														</Button>
														<Button
															size='small'
															onClick={() =>
																action(() => revokeStage3Access(token, row.id), 'Cofnięto dostęp.', 'requests')
															}
														>
															Cofnij dostęp
														</Button>
													</Stack>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</Box>
					)}

					{tab === 4 && (
						<Box>
							<Typography mb={2}>Domyślne okna czasowe</Typography>
							<Stack
								direction='row'
								gap={1}
								alignItems='center'
							>
								<TextField
									label='Godziny na otwarcie (zaproszenie)'
									size='small'
									type='number'
									value={settings.invitation_hours}
									onChange={(e) => setSettings((prev) => ({ ...prev, invitation_hours: Number(e.target.value || 48) }))}
								/>
								<TextField
									label='Minuty dostępu po otwarciu'
									size='small'
									type='number'
									value={settings.access_minutes}
									onChange={(e) => setSettings((prev) => ({ ...prev, access_minutes: Number(e.target.value || 120) }))}
								/>
								<TextField
									label='Tekst watermark (szablon)'
									size='small'
									value={settings.watermark_template}
									onChange={(e) =>
										setSettings((prev) => ({ ...prev, watermark_template: String(e.target.value || '') }))
									}
									sx={{ minWidth: 420 }}
									helperText='Pola: {email}, {student_name}, {work_code}, {doc_type}, {datetime}'
								/>
								<Button
									variant='contained'
									disabled={loading}
									onClick={() => action(() => saveStage3DocSettings(token, settings), 'Zapisano ustawienia.', 'none')}
								>
									Zapisz
								</Button>
							</Stack>
							<Stack
								direction='row'
								gap={2}
								mt={2}
								mb={1}
								flexWrap='wrap'
								alignItems='center'
							>
								<Button
									variant={settings.view_enabled ? 'contained' : 'outlined'}
									color={settings.view_enabled ? 'success' : 'inherit'}
									onClick={() => setSettings((prev) => ({ ...prev, view_enabled: !prev.view_enabled }))}
								>
									Wgląd do sprawdzonych prac: {settings.view_enabled ? 'WŁĄCZONY' : 'WYŁĄCZONY'}
								</Button>
								<Button
									variant={settings.request_enabled ? 'contained' : 'outlined'}
									color={settings.request_enabled ? 'success' : 'inherit'}
									onClick={() => setSettings((prev) => ({ ...prev, request_enabled: !prev.request_enabled }))}
								>
									Zgłaszanie próśb: {settings.request_enabled ? 'WŁĄCZONE' : 'WYŁĄCZONE'}
								</Button>
							</Stack>
							<Stack
								gap={1}
								mt={1}
							>
								<TextField
									label='Temat maila po auto-przejściu do READY'
									size='small'
									value={settings.ready_mail_subject}
									onChange={(e) =>
										setSettings((prev) => ({ ...prev, ready_mail_subject: String(e.target.value || '') }))
									}
									sx={{ minWidth: 640 }}
									helperText='Pola: {first_name}, {last_name}, {full_name}, {work_code}'
								/>
								<TextField
									label='Treść maila po auto-przejściu do READY'
									size='small'
									multiline
									minRows={5}
									value={settings.ready_mail_body}
									onChange={(e) => setSettings((prev) => ({ ...prev, ready_mail_body: String(e.target.value || '') }))}
									sx={{ minWidth: 760 }}
									helperText='Pola: {first_name}, {last_name}, {full_name}, {work_code}'
								/>
							</Stack>
						</Box>
					)}
				</DialogContent>
			</Dialog>
			<Menu
				anchorEl={docActionsAnchorEl}
				open={Boolean(docActionsAnchorEl && docActionsContext)}
				onClose={closeDocActionsMenu}
			>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							openCompletenessStudentLikePreview(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Widok ucznia
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							openCompletenessDocument(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Podgląd dokumentu
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							openCompletenessGoogleDocsEdit(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Edycja dokumentu (Dokumenty Google)
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							editCompletenessPageRotations(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Obrót widoku 90°/180°/270° (safe)
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							void editCompletenessCommentOffsetY(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Przesunięcie komentarzy — komY (px)
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							void editCompletenessCommentOffsetX(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Przesunięcie komentarzy — komX (px)
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							void editCompletenessCommentScale(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Skala nakładki komentarzy X,Y % (od środka)
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							void editCompletenessToggleHideOverlayHighlightBoxes(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Przełącz: ukryj żółte prostokąty (HTML)
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							void editCompletenessToggleHidePdfAnnotationRender(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Przełącz: wyłącz adnotacje PDF na obrazie (pdf.js)
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							void editCompletenessCommentRotationOnly(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Obrót tylko komentarzy (obraz bez zmian)
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							createAggressiveRotatedCopy(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Kopia agresywna (trwały obrót)
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							void editCompletenessDocAssignment(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Edytuj przypisanie (typ / zakres stron)
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (docActionsContext) {
							void removeCompletenessDocumentLink(docActionsContext.row, docActionsContext.docType);
						}
						closeDocActionsMenu();
					}}
				>
					Usuń powiązanie dokumentu
				</MenuItem>
			</Menu>
			<Dialog
				open={previewOpen}
				onClose={() => setPreviewOpen(false)}
				fullScreen
			>
				<DialogTitle>
					Podgląd pliku: {previewItem?.drive_file_name || '-'}
					<Typography
						variant='body2'
						sx={{ mt: 0.5 }}
					>
						Źródło: {previewScopeLabel} | Komisja: {previewItem?.jury_pair_name || '-'} | Ścieżka:{' '}
						{folderPathLabel(previewItem)}
					</Typography>
					<Typography
						variant='body2'
						sx={{ mt: 0.5 }}
					>
						Uczeń: {previewItem?.student_name || '-'} | Kod: {previewItem?.work_code || '-'} | Typ:{' '}
						{previewItem?.document_type_label || docTypeLabel(String(previewItem?.document_type || '')) || '-'}
					</Typography>
					{isAdmin && previewItem?.preview_context === 'completeness' && (
						<Box
							sx={{
								mt: 1,
								p: 1,
								borderRadius: 1,
								bgcolor: 'action.hover',
								border: '1px solid',
								borderColor: 'divider',
							}}
						>
							<Stack
								direction='row'
								flexWrap='wrap'
								alignItems='baseline'
								columnGap={1.5}
								rowGap={0.5}
							>
								<Typography
									component='span'
									sx={{ fontWeight: 700, fontSize: '1.05rem' }}
								>
									Weryfikacja (admin)
								</Typography>
								<Typography
									component='span'
									sx={{
										fontWeight: 700,
										fontSize: '1.35rem',
										lineHeight: 1.2,
										letterSpacing: 0.03,
									}}
								>
									{previewItem?.work_code || '—'}
								</Typography>
								<Typography
									component='span'
									sx={{ fontSize: '1.0625rem', lineHeight: 1.45 }}
								>
									· Uczeń: {previewItem?.student_name || '—'} · IIIA:{' '}
									{previewItem?.third_a_stage_points != null && previewItem?.third_a_stage_points !== ''
										? String(previewItem.third_a_stage_points)
										: '—'}{' '}
									· J1:{' '}
									{previewItem?.third_a_juror1_points != null && previewItem?.third_a_juror1_points !== ''
										? String(previewItem.third_a_juror1_points)
										: '—'}{' '}
									· J2:{' '}
									{previewItem?.third_a_juror2_points != null && previewItem?.third_a_juror2_points !== ''
										? String(previewItem.third_a_juror2_points)
										: '—'}
								</Typography>
							</Stack>
							{(() => {
								const dt = String(previewItem?.document_type || '');
								const shared = Boolean(previewItem?.shared_review);
								const j1 = String(previewItem?.assigned_juror1_name || '').trim();
								const j2 = String(previewItem?.assigned_juror2_name || '').trim();
								const pair = String(previewItem?.assigned_jury_pair_name || '').trim();
								const hasTeam = pair || j1 || j2;
								if (!hasTeam) {
									return null;
								}
								if (dt === 'review_1' || dt === 'review_2') {
									if (shared) {
										return (
											<Typography
												variant='body1'
												component='div'
												sx={{ mt: 0.75, fontSize: '1.0625rem', lineHeight: 1.45 }}
											>
												Recenzja wspólna — jurorzy: {j1 || '—'}, {j2 || '—'}
												{pair ? ` · Para: ${pair}` : ''}
											</Typography>
										);
									}
									if (dt === 'review_1') {
										return (
											<Typography
												variant='body1'
												component='div'
												sx={{ mt: 0.75, fontSize: '1.0625rem', lineHeight: 1.45 }}
											>
												Recenzja jurora 1 — juror: {j1 || '—'}
												{pair ? ` · Para: ${pair}` : ''}
											</Typography>
										);
									}
									return (
										<Typography
											variant='body1'
											component='div'
											sx={{ mt: 0.75, fontSize: '1.0625rem', lineHeight: 1.45 }}
										>
											Recenzja jurora 2 — juror: {j2 || '—'}
											{pair ? ` · Para: ${pair}` : ''}
										</Typography>
									);
								}
								return (
									<Typography
										variant='body1'
										component='div'
										sx={{ mt: 0.75, fontSize: '1.0625rem', lineHeight: 1.45 }}
									>
										{pair ? `Para: ${pair}` : ''}
										{pair && (j1 || j2) ? ' · ' : ''}
										{j1 || j2 ? `Jurorzy w parze: ${[j1, j2].filter(Boolean).join(', ')}` : ''}
									</Typography>
								);
							})()}
						</Box>
					)}
				</DialogTitle>
				<DialogContent>
					<Stack
						direction='row'
						gap={1}
						flexWrap='wrap'
						mb={1}
					>
						<Button
							variant={previewMode === 'preview' ? 'contained' : 'outlined'}
							onClick={() => setPreviewMode('preview')}
						>
							Podgląd
						</Button>
						<Button
							variant='outlined'
							onClick={() => {
								if (!previewItem?.drive_file_id) return;
								window.open(getDriveViewUrl(previewItem.drive_file_id), '_blank', 'noopener,noreferrer');
							}}
						>
							Przeglądaj
						</Button>
						<Button
							variant='outlined'
							onClick={() => {
								if (!previewItem?.drive_file_id) return;
								const editUrl =
									previewItem?.preview_context === 'completeness'
										? getDriveEditUrlForIndexItem({
												drive_file_id: previewItem.drive_file_id,
												drive_mime_type: previewItem.drive_mime_type,
											})
										: '';
								const url = editUrl || getDriveEditUrl(String(previewItem.drive_file_id));
								window.open(url, '_blank', 'noopener,noreferrer');
							}}
							title={
								previewItem?.preview_context === 'completeness'
									? getDriveEditTooltipForIndexItem(previewItem)
									: undefined
							}
						>
							Edytuj
						</Button>
						<Divider
							orientation='vertical'
							flexItem
						/>
						<Button
							onClick={async () => {
								const commentsFileIndexId = Number(
									previewItem?.source_file_index_id ||
										(previewScopeLabel === 'Indeks plików' ? previewItem?.id : 0) ||
										0,
								);
								if (commentsFileIndexId <= 0) {
									onMessage('Brak powiązania z indeksem plików dla komentarzy.');
									return;
								}
								setLoading(true);
								try {
									const response = await getStage3FileComments(token, commentsFileIndexId);
									setSelectedComments(response?.data?.items || []);
									setSelectedCommentsMeta(response?.data?.meta || null);
									setSelectedCommentsFile(previewItem.drive_file_name || `plik #${previewItem.id}`);
									onMessage('Pobrano komentarze.');
								} catch (error: any) {
									onMessage(error?.response?.data?.message || 'Nie udało się pobrać komentarzy.');
								} finally {
									setLoading(false);
								}
							}}
						>
							Komentarze
						</Button>
						<Button
							onClick={() => {
								if (!previewItem) return;
								if (previewItem?.matched_work_code) {
									fillLinkByCode(previewItem);
								} else {
									setTab(1);
									if (previewItem?.student_id) {
										setNewLink((prev) => ({
											...prev,
											student_id: String(previewItem.student_id),
											work_code: String(previewItem.work_code || ''),
											drive_file_id: String(previewItem.drive_file_id || ''),
											file_index_id: previewItem.source_file_index_id ? String(previewItem.source_file_index_id) : '',
											document_type: String(previewItem.document_type || prev.document_type),
										}));
									}
								}
								setPreviewOpen(false);
							}}
						>
							Przypisz w Dokumentach
						</Button>
						{previewItem?.document_id && (
							<Button
								onClick={async () => {
									const documentRow = documentsById.get(Number(previewItem.document_id));
									if (!documentRow) {
										onMessage('Nie znaleziono przypisania dokumentu do edycji.');
										return;
									}
									await editDocumentAssignment(documentRow);
								}}
							>
								Edytuj przypisanie
							</Button>
						)}
						{previewItem?.source_file_index_id && (
							<Button
								onClick={() => {
									action(
										() => setStage3FileStatus(token, Number(previewItem.source_file_index_id), { status: 'assigned' }),
										'Oznaczono jako przypisany.',
										'index',
									).then(() => goToNextInWorkflow());
								}}
							>
								Oznacz przypisany i następny
							</Button>
						)}
						{!previewItem?.source_file_index_id && previewItem?.id && previewScopeLabel === 'Indeks plików' && (
							<Button
								onClick={() => {
									action(
										() => setStage3FileStatus(token, Number(previewItem.id), { status: 'assigned' }),
										'Oznaczono jako przypisany.',
										'index',
									).then(() => goToNextInWorkflow());
								}}
							>
								Oznacz przypisany i następny
							</Button>
						)}
						<Button
							onClick={goToNextInWorkflow}
							disabled={previewCursor < 0 || previewCursor + 1 >= previewItems.length}
						>
							Następny
						</Button>
						<Button onClick={() => setPreviewOpen(false)}>Zamknij</Button>
					</Stack>
					<Box sx={{ display: 'grid', gridTemplateColumns: selectedComments.length ? '2fr 1fr' : '1fr', gap: 1 }}>
						<Box sx={{ border: '1px solid #ddd', minHeight: '70vh' }}>
							<iframe
								title='stage3-file-preview'
								src={getPreviewSrc()}
								style={{ width: '100%', height: '70vh', border: 0 }}
							/>
						</Box>
						{selectedComments.length > 0 && (
							<Box sx={{ border: '1px solid #ddd', p: 1, overflow: 'auto', maxHeight: '70vh' }}>
								<Typography
									variant='subtitle2'
									sx={{ mb: 1 }}
								>
									Komentarze ({selectedComments.length})
								</Typography>
								{selectedComments.map((row: any, idx: number) => (
									<Box
										key={`${idx}-${row.no || ''}`}
										sx={{ mb: 1, pb: 1, borderBottom: '1px solid #eee' }}
									>
										<Typography
											variant='caption'
											sx={{ display: 'block', color: '#666' }}
										>
											#{row.no || idx + 1} {row.type ? `(${row.type})` : ''}
										</Typography>
										{row.quoted && (
											<Typography
												variant='caption'
												sx={{ display: 'block', color: '#444' }}
											>
												Fragment: {row.quoted}
											</Typography>
										)}
										<Typography variant='body2'>{row.content || row.htmlContent || '-'}</Typography>
									</Box>
								))}
							</Box>
						)}
					</Box>
				</DialogContent>
			</Dialog>
			<StudentWorkViewer
				open={studentLikeViewerOpen}
				onClose={() => {
					setStudentLikeViewerOpen(false);
					setStudentLikeAdminMeta(null);
				}}
				documentId={studentLikeDocumentId}
				url={studentLikeDocumentId ? getStage3DocumentAdminStreamUrl(studentLikeDocumentId) : ''}
				title={studentLikeTitle}
				watermark={
					studentLikeWatermark ||
					buildWatermarkLabel(settings.watermark_template, { email: 'biuro', datetime: new Date().toLocaleString() })
				}
				token={token}
				commentsFetcher={(documentId: number) => getStage3DocumentCommentsAdmin(token, documentId)}
				onAccessError={(message) => onMessage(message || 'Brak aktywnego dostępu.')}
				pageRotations={studentLikePageRotations}
				commentOffsetYByPage={studentLikeCommentOffsetY}
				commentOffsetYGlobal={studentLikeCommentOffsetYGlobal}
				commentOffsetXByPage={studentLikeCommentOffsetX}
				commentOffsetXGlobal={studentLikeCommentOffsetXGlobal}
				commentScaleXPercent={studentLikeCommentScaleX}
				commentScaleYPercent={studentLikeCommentScaleY}
				hideOverlayHighlightBoxes={studentLikeHideOverlayHighlightBoxes}
				hidePdfAnnotationRender={studentLikeHidePdfAnnotationRender}
				commentRotationDegByPage={studentLikeCommentRotationDeg}
				adminPreviewMeta={studentLikeAdminMeta}
				showRotationInfo
			/>
			<Dialog
				open={splitPreviewOpen}
				onClose={() => setSplitPreviewOpen(false)}
				fullWidth
				maxWidth='xl'
			>
				<DialogTitle sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 2 }}>
					<Box>
						<Typography variant='h5'>Podgląd splitu (test)</Typography>
						<Typography
							variant='subtitle1'
							sx={{ mt: 0.5 }}
						>
							{splitPreviewSourceDriveFileName || splitPreviewItem?.drive_file_name || '-'}
						</Typography>
						<Typography
							variant='body2'
							sx={{ mt: 0.5 }}
						>
							MIME: {splitPreviewSourceDriveMimeType || mimeLabel(splitPreviewItem)} | Komisja:{' '}
							{splitPreviewItem?.jury_pair_name || '-'} | Ścieżka: {folderPathLabel(splitPreviewItem)}
						</Typography>
					</Box>
					<IconButton onClick={() => setSplitPreviewOpen(false)}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					{splitPreviewSourceDriveMimeType !== '' &&
						String(splitPreviewSourceDriveMimeType).toLowerCase() !== 'application/vnd.google-apps.document' && (
							<Alert
								severity='warning'
								sx={{ mb: 2 }}
							>
								Ten plik nie jest Google Docs. Podział HTML najlepiej działa dla `application/vnd.google-apps.document`.
								Dla `DOCX` zalecana jest najpierw konwersja do Google Docs, a potem split.
							</Alert>
						)}
					{splitPreviewBusy !== 'idle' ? (
						<Alert
							severity='info'
							sx={{ mb: 1 }}
						>
							{splitPreviewBusy === 'analyzing'
								? 'Trwa analiza dokumentu źródłowego...'
								: 'Trwa rozdzielanie recenzji i tworzenie plików...'}
						</Alert>
					) : null}
					{splitPreviewError ? (
						<Alert severity='error'>{splitPreviewError}</Alert>
					) : splitPreviewRows.length === 0 ? (
						<>
							<Alert severity='info'>Brak wykrytych fragmentów do splitu.</Alert>
							{splitPreviewHtml ? (
								<Box mt={2}>
									<Typography variant='subtitle2'>HTML źródła (pełna treść)</Typography>
									<Paper
										variant='outlined'
										sx={{ p: 1, maxHeight: 280, overflow: 'auto', bgcolor: '#fafafa' }}
									>
										<Box
											component='pre'
											sx={{ m: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}
										>
											{splitPreviewHtml}
										</Box>
									</Paper>
								</Box>
							) : null}
						</>
					) : (
						<>
							<Table
								size='small'
								stickyHeader
							>
								<TableHead>
									<TableRow>
										<TableCell>Podziel</TableCell>
										<TableCell>Lp. w pliku</TableCell>
										<TableCell>Kod pracy</TableCell>
										<TableCell>Lp. recenzji (dla kodu)</TableCell>
										<TableCell>Dopasowanie</TableCell>
										<TableCell>Punkty (wynik)</TableCell>
										<TableCell>Juror 1</TableCell>
										<TableCell>Juror 2</TableCell>
										<TableCell>Z recenzji</TableCell>
										<TableCell>Znaki</TableCell>
										<TableCell>Podgląd / pełny tekst</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{splitPreviewRows.map((row: any, idx: number) => (
										<TableRow key={`${row.work_code || '-'}-${row.review_no || idx}-${idx}`}>
											<TableCell>
												<Checkbox
													size='small'
													checked={splitPreviewSelectedSegmentIds.includes(Number(row.segment_id || 0))}
													disabled={!row.splittable}
													onChange={(_, checked) => toggleSplitSegment(Number(row.segment_id || 0), checked)}
												/>
											</TableCell>
											<TableCell>{row.file_review_no || idx + 1}</TableCell>
											<TableCell>
												<Typography variant='body2'>{row.work_code_display || row.work_code || '-'}</Typography>
												{row.note ? (
													<Typography
														variant='caption'
														sx={{ color: '#666' }}
													>
														{row.note}
													</Typography>
												) : null}
											</TableCell>
											<TableCell>{row.review_no || '-'}</TableCell>
											<TableCell>
												<Chip
													size='small'
													label={splitMatchSourceLabel(String(row.match_source || ''))}
													color={String(row.match_source || '') === 'exact' ? 'success' : 'default'}
												/>
											</TableCell>
											<TableCell>
												{row.db_final_points !== null && row.db_final_points !== undefined
													? `${row.db_final_points}/${thirdAMaxPoints}`
													: '-'}
											</TableCell>
											<TableCell sx={{ minWidth: 150 }}>
												{row.splittable ? (
													<Stack
														direction='row'
														spacing={0.5}
														alignItems='center'
													>
														<TextField
															size='small'
															placeholder='0-30'
															value={splitJuror1PointDrafts[Number(row.segment_id || 0)] ?? ''}
															onChange={(e) =>
																setSplitJuror1PointDrafts((prev) => ({
																	...prev,
																	[Number(row.segment_id || 0)]: String(e.target.value || ''),
																}))
															}
															sx={{ width: 84, '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
															disabled={splitAssignMode !== 'review_1'}
														/>
														<Tooltip
															title={
																splitAssignMode !== 'review_1'
																	? 'Edycja aktywna po wyborze Juror 1 w trybie przypisania.'
																	: 'Zapisz ocenę Jurora 1 dla wybranego kodu'
															}
														>
															<span>
																<IconButton
																	size='small'
																	onClick={() => saveSplitJurorPoints(row, 'review_1')}
																	disabled={
																		splitAssignMode !== 'review_1' ||
																		splitJurorPointSavingIds.includes(Number(row.segment_id || 0))
																	}
																>
																	{splitJurorPointSavingIds.includes(Number(row.segment_id || 0)) ? (
																		<CircularProgress size={14} />
																	) : (
																		<SaveIcon fontSize='small' />
																	)}
																</IconButton>
															</span>
														</Tooltip>
													</Stack>
												) : (
													<Typography
														variant='caption'
														sx={{ color: '#777' }}
													>
														-
													</Typography>
												)}
											</TableCell>
											<TableCell sx={{ minWidth: 150 }}>
												{row.splittable ? (
													<Stack
														direction='row'
														spacing={0.5}
														alignItems='center'
													>
														<TextField
															size='small'
															placeholder='0-30'
															value={splitJuror2PointDrafts[Number(row.segment_id || 0)] ?? ''}
															onChange={(e) =>
																setSplitJuror2PointDrafts((prev) => ({
																	...prev,
																	[Number(row.segment_id || 0)]: String(e.target.value || ''),
																}))
															}
															sx={{ width: 84, '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
															disabled={splitAssignMode !== 'review_2'}
														/>
														<Tooltip
															title={
																splitAssignMode !== 'review_2'
																	? 'Edycja aktywna po wyborze Juror 2 w trybie przypisania.'
																	: 'Zapisz ocenę Jurora 2 dla wybranego kodu'
															}
														>
															<span>
																<IconButton
																	size='small'
																	onClick={() => saveSplitJurorPoints(row, 'review_2')}
																	disabled={
																		splitAssignMode !== 'review_2' ||
																		splitJurorPointSavingIds.includes(Number(row.segment_id || 0))
																	}
																>
																	{splitJurorPointSavingIds.includes(Number(row.segment_id || 0)) ? (
																		<CircularProgress size={14} />
																	) : (
																		<SaveIcon fontSize='small' />
																	)}
																</IconButton>
															</span>
														</Tooltip>
													</Stack>
												) : (
													<Typography
														variant='caption'
														sx={{ color: '#777' }}
													>
														-
													</Typography>
												)}
											</TableCell>
											<TableCell>
												{row.points_extracted !== null && row.points_extracted !== undefined
													? `${row.points_extracted}/${thirdAMaxPoints}`
													: '-'}
												{isExtractedMismatchForSelectedJuror(row) ? (
													<Box mt={0.5}>
														<Button
															size='small'
															variant='text'
															onClick={() => applyExtractedPointsToSelectedJuror(row)}
														>
															Przepisz
														</Button>
													</Box>
												) : null}
											</TableCell>
											<TableCell>
												{splitPreviewShowSanitized ? row.sanitized_chars || row.chars || 0 : row.chars || 0}
											</TableCell>
											<TableCell sx={{ minWidth: 420 }}>
												{(() => {
													const segmentId = Number(row.segment_id || 0);
													const expanded = splitPreviewExpandedSegmentIds.includes(segmentId);
													const fullText = String(
														(splitPreviewShowSanitized ? row.sanitized_full_text : row.full_text) ||
															(splitPreviewShowSanitized ? row.full_text : row.sanitized_full_text) ||
															'',
													).replace(/\n{3,}/g, '\n\n');
													const previewLimit = 150;
													if (!fullText) {
														return (
															<Typography variant='body2'>
																{splitPreviewShowSanitized
																	? row.sanitized_text_preview || row.text_preview || '-'
																	: row.text_preview || row.sanitized_text_preview || '-'}
															</Typography>
														);
													}
													const previewPart = fullText.slice(0, previewLimit);
													const missingPart = fullText.slice(previewLimit);
													if (!missingPart) {
														return <Box sx={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5 }}>{fullText}</Box>;
													}
													return (
														<Box sx={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5 }}>
															{previewPart}
															{expanded ? (
																<>
																	{missingPart}
																	<Box
																		component='span'
																		onClick={() => toggleSplitTextExpand(segmentId, false)}
																		sx={{ cursor: 'pointer', color: 'primary.main', ml: 0.5 }}
																	>
																		[zwiń]
																	</Box>
																</>
															) : (
																<Box
																	component='span'
																	onClick={() => toggleSplitTextExpand(segmentId, true)}
																	sx={{ cursor: 'pointer', color: 'primary.main' }}
																>
																	...
																</Box>
															)}
														</Box>
													);
												})()}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
							{splitPreviewAlerts.length > 0 && (
								<Alert
									severity='warning'
									sx={{ mt: 2 }}
								>
									{splitPreviewAlerts.join(' | ')}
								</Alert>
							)}
							{splitPreviewCorrections.length > 0 && (
								<Alert
									severity='info'
									sx={{ mt: 1 }}
								>
									Korekty kodów: {splitPreviewCorrections.join(' | ')}
								</Alert>
							)}
							{splitPreviewForeignCodes.length > 0 && (
								<Alert
									severity='error'
									sx={{ mt: 1 }}
								>
									Kody z innej komisji: {splitPreviewForeignCodes.join(', ')}
								</Alert>
							)}
							{splitPreviewPotentialCodes.length > 0 && (
								<Alert
									severity='info'
									sx={{ mt: 1 }}
								>
									Potencjalne kody (format XXXX-XXXX): {splitPreviewPotentialCodes.join(', ')}
								</Alert>
							)}
							{splitPreviewResultItems.length > 0 && (
								<Box mt={2}>
									<Typography
										variant='subtitle2'
										gutterBottom
									>
										Utworzone pliki po rozdzieleniu
									</Typography>
									<Table size='small'>
										<TableHead>
											<TableRow>
												<TableCell>Plik</TableCell>
												<TableCell>Kod pracy</TableCell>
												<TableCell>Znaki</TableCell>
												<TableCell>Punkty</TableCell>
												<TableCell>Zapis</TableCell>
												<TableCell>Przydział</TableCell>
												<TableCell>Akcje</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{splitPreviewResultItems.map((created: any, idx: number) => {
												const assignment = splitPreviewResultAssignments.find(
													(a: any) => Number(a.file_index_id) === Number(created.file_index_id),
												);
												return (
													<TableRow key={`${created.drive_file_id || idx}`}>
														<TableCell>{created.name || created.drive_file_id || '-'}</TableCell>
														<TableCell>{created.work_code || '-'}</TableCell>
														<TableCell>{created.chars || '-'}</TableCell>
														<TableCell>
															{created.points !== null && created.points !== undefined
																? `${created.points}/${thirdAMaxPoints}`
																: '-'}
														</TableCell>
														<TableCell>
															<Chip
																size='small'
																label={created.overwritten ? 'Nadpisano' : 'Nowy'}
																color={created.overwritten ? 'warning' : 'success'}
															/>
														</TableCell>
														<TableCell>
															{assignment
																? `${assignment.document_type || '-'} / student_id: ${assignment.student_id || '-'}`
																: 'brak'}
														</TableCell>
														<TableCell>
															<Tooltip title='Podgląd pliku na Drive'>
																<IconButton
																	size='small'
																	onClick={() =>
																		window.open(
																			getDriveViewUrl(String(created.drive_file_id || '')),
																			'_blank',
																			'noopener,noreferrer',
																		)
																	}
																>
																	<PreviewIcon fontSize='small' />
																</IconButton>
															</Tooltip>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</Box>
							)}
						</>
					)}
					<Stack
						direction='row'
						gap={2}
						mt={2}
						alignItems='center'
						flexWrap='wrap'
					>
						<Stack
							direction='row'
							alignItems='center'
						>
							<Checkbox
								checked={splitAutoAssign}
								onChange={(_, checked) => setSplitAutoAssign(checked)}
								size='small'
							/>
							<Typography variant='body2'>Po rozdzieleniu przypisz do kompletności</Typography>
						</Stack>
						<FormControl
							size='small'
							sx={{ minWidth: 260 }}
							disabled={!splitAutoAssign}
						>
							<InputLabel>Tryb przypisania recenzji</InputLabel>
							<Select
								label='Tryb przypisania recenzji'
								value={splitAssignMode}
								onChange={(e) => setSplitAssignMode(e.target.value as '' | 'review_1' | 'review_2' | 'shared')}
							>
								<MenuItem value=''>Wybierz jurora</MenuItem>
								<MenuItem value='review_1'>{splitAssignModeLabel('review_1', splitPreviewItem)}</MenuItem>
								<MenuItem value='review_2'>{splitAssignModeLabel('review_2', splitPreviewItem)}</MenuItem>
								<MenuItem value='shared'>{splitAssignModeLabel('shared', splitPreviewItem)}</MenuItem>
							</Select>
						</FormControl>
						<Stack
							direction='row'
							alignItems='center'
						>
							<Checkbox
								checked={splitRemovePoints}
								onChange={(_, checked) => {
									setSplitRemovePoints(checked);
									if (splitPreviewShowSanitized && splitPreviewItem) {
										openSplitPreview(splitPreviewItem, false);
									}
								}}
								size='small'
							/>
							<Typography variant='body2'>Usuń punkty z recenzji ucznia</Typography>
						</Stack>
						<Stack
							direction='row'
							alignItems='center'
						>
							<Checkbox
								checked={splitRemoveWorkCode}
								onChange={(_, checked) => {
									setSplitRemoveWorkCode(checked);
									if (splitPreviewShowSanitized && splitPreviewItem) {
										openSplitPreview(splitPreviewItem, false);
									}
								}}
								size='small'
							/>
							<Typography variant='body2'>Usuń kod pracy z recenzji ucznia</Typography>
						</Stack>
						<TextField
							size='small'
							label='Usuń słowa/zwroty (oddziel ; )'
							value={splitStripTermsRaw}
							onChange={(e) => setSplitStripTermsRaw(String(e.target.value || ''))}
							sx={{ minWidth: 320 }}
						/>
						<Stack
							direction='row'
							alignItems='center'
						>
							<Checkbox
								checked={splitPreviewShowSanitized}
								onChange={(_, checked) => {
									setSplitPreviewShowSanitized(checked);
									if (checked && splitPreviewItem) {
										openSplitPreview(splitPreviewItem, false);
									}
								}}
								size='small'
							/>
							<Typography variant='body2'>Podgląd oczyszczonej wersji</Typography>
							<Chip
								size='small'
								label={splitPreviewShowSanitized ? 'OCZYSZCZONA' : 'ORYGINAŁ'}
								color={splitPreviewShowSanitized ? 'success' : 'default'}
								sx={{ ml: 1 }}
							/>
						</Stack>
						<Typography
							variant='caption'
							sx={{ color: '#666' }}
						>
							Tryb oczyszczony zmienia treść recenzji, nie metadane tabeli (kod/punkty).
						</Typography>
						<Button
							size='small'
							variant='outlined'
							disabled={!canBulkApplyExtractedPoints() || splitPreviewBusy !== 'idle'}
							onClick={applyAllExtractedPointsToSelectedJuror}
						>
							Przepisz wszystkie punkty
						</Button>
					</Stack>
					<Stack
						direction='row'
						gap={1}
						mt={2}
					>
						<Button
							variant='outlined'
							disabled={!splitPreviewSourceDriveFileId}
							onClick={() => {
								if (!splitPreviewSourceDriveFileId) return;
								window.open(getDriveEditUrl(splitPreviewSourceDriveFileId), '_blank', 'noopener,noreferrer');
							}}
						>
							Edytuj źródło
						</Button>
						{!(
							isImportReviewDerivedRow(splitPreviewItem || {}) ||
							isImportReviewCopyName(splitPreviewSourceDriveFileName || splitPreviewItem?.drive_file_name || '')
						) && (
							<Button
								variant='outlined'
								disabled={!splitPreviewSourceFileIndexId || splitPreviewBusy !== 'idle'}
								onClick={async () => {
									if (!splitPreviewSourceFileIndexId || !splitPreviewItem) return;
									setSplitPreviewBusy('analyzing');
									setLoading(true);
									try {
										const response = await createStage3ImportCopy(token, Number(splitPreviewSourceFileIndexId));
										const copied = response?.data || {};
										const nextItem = {
											...splitPreviewItem,
											id: Number(copied.id || splitPreviewSourceFileIndexId),
											drive_file_id: String(copied.drive_file_id || ''),
											drive_file_name: String(copied.drive_file_name || ''),
											drive_mime_type: String(copied.drive_mime_type || ''),
											file_category: String(copied.file_category || 'combined_review'),
										};
										onMessage('Utworzono kopię importu recenzji.');
										await openSplitPreview(nextItem, false);
									} catch (error: any) {
										onMessage(error?.response?.data?.message || 'Nie udało się utworzyć kopii importu recenzji.');
									} finally {
										setSplitPreviewBusy('idle');
										setLoading(false);
									}
								}}
							>
								Kopia importu recenzji
							</Button>
						)}
						<Button
							variant='outlined'
							disabled={splitPreviewBusy !== 'idle'}
							onClick={() => {
								if (!splitPreviewItem) return;
								openSplitPreview(splitPreviewItem, false);
							}}
							startIcon={splitPreviewBusy === 'analyzing' ? <CircularProgress size={16} /> : undefined}
						>
							Przeanalizuj ponownie
						</Button>
						<Button
							variant='contained'
							disabled={
								!splitPreviewItem ||
								!splitPreviewSourceFileIndexId ||
								splitPreviewSelectedSegmentIds.length === 0 ||
								(splitAutoAssign && splitAssignMode === '') ||
								splitPreviewBusy !== 'idle'
							}
							onClick={runSplitNow}
							startIcon={splitPreviewBusy === 'splitting' ? <CircularProgress size={16} /> : undefined}
						>
							Rozdziel recenzje
						</Button>
						<Button onClick={() => setSplitPreviewOpen(false)}>Zamknij</Button>
					</Stack>
				</DialogContent>
			</Dialog>
			<Dialog
				open={splitSummaryOpen}
				onClose={() => setSplitSummaryOpen(false)}
				fullWidth
				maxWidth='lg'
			>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<Typography variant='h6'>Podsumowanie po rozdzieleniu recenzji</Typography>
					<IconButton onClick={() => setSplitSummaryOpen(false)}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<Typography
						variant='body2'
						sx={{ mb: 1, color: '#555' }}
					>
						Plik źródłowy: {splitPreviewSourceDriveFileName || '-'}
					</Typography>
					{splitPreviewResultItems.length === 0 ? (
						<Alert severity='info'>Brak utworzonych plików w ostatnim rozdzieleniu.</Alert>
					) : (
						<Table size='small'>
							<TableHead>
								<TableRow>
									<TableCell>Lp.</TableCell>
									<TableCell>Plik</TableCell>
									<TableCell>Kod pracy</TableCell>
									<TableCell>Znaki</TableCell>
									<TableCell>Punkty</TableCell>
									<TableCell>Zapis</TableCell>
									<TableCell>Przydział</TableCell>
									<TableCell>Podgląd</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{splitPreviewResultItems.map((created: any, idx: number) => {
									const assignment = splitPreviewResultAssignments.find(
										(a: any) => Number(a.file_index_id) === Number(created.file_index_id),
									);
									return (
										<TableRow key={`${created.drive_file_id || idx}`}>
											<TableCell>{idx + 1}</TableCell>
											<TableCell>{created.drive_file_name || created.drive_file_id || '-'}</TableCell>
											<TableCell>{created.work_code || '-'}</TableCell>
											<TableCell>{created.chars || '-'}</TableCell>
											<TableCell>
												{created.points !== null && created.points !== undefined
													? `${created.points}/${thirdAMaxPoints}`
													: '-'}
											</TableCell>
											<TableCell>
												<Chip
													size='small'
													label={created.overwritten ? 'Nadpisano' : 'Nowy'}
													color={created.overwritten ? 'warning' : 'success'}
												/>
											</TableCell>
											<TableCell>
												{assignment
													? `${assignment.document_type || '-'} / student_id: ${assignment.student_id || '-'}`
													: 'brak'}
											</TableCell>
											<TableCell>
												<Tooltip title='Podgląd pliku na Drive'>
													<IconButton
														size='small'
														onClick={() =>
															window.open(
																getDriveViewUrl(String(created.drive_file_id || '')),
																'_blank',
																'noopener,noreferrer',
															)
														}
													>
														<PreviewIcon fontSize='small' />
													</IconButton>
												</Tooltip>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					)}
					<Stack
						direction='row'
						justifyContent='flex-end'
						mt={2}
					>
						<Button onClick={() => setSplitSummaryOpen(false)}>Zamknij</Button>
					</Stack>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default Stage3DocumentAccessDialog;
